import { useLinkPromoPreview } from "../state/link-promo-preview";
import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import type { WidgetBrand, WidgetService, WidgetUnit, WidgetEvent,
  WidgetSpecialDateEvent,
} from "../types";
import { supabase } from "../config";
import { useCart } from "../state/cart";
import { optimizedImageUrl, optimizedSrcSet, IMG_WIDTHS } from "../lib/image";
import { showWhatsMissing } from "../lib/show-whats-missing";
import { FloorPlanPicker, type SeatSelection } from "./FloorPlanPicker";
import { BookingSetup } from "./BookingSetup";
import { DineEnhancements } from "./DineEnhancements";
import {
  SeatingAreaPicker,
  SeatingAreaField,
  SeatingAreaPreview,
  useSeatingAreas,
} from "./SeatingAreaPicker";
import {
  FixedMenuBanner,
  FixedMenuSummary,
  FixedPartyPicker,
  GuestMenuSplitPicker,
  matchSpecialDateEvent,
} from "./FixedMenuPanel";
import {
  type BookingWindow,
  type CapacityResponse,
  checkCapacity,
  createBookingHold,
  fetchSeatAvailability,
  fetchRoomAvailability,
  type RoomAvailability,
  getCartToken,
  humanWindow,
  stampVisitPartySize,
} from "../state/booking";
import { isServiceBookable } from "../state/capacity";
import { fieldLabel } from "../state/field-labels";
import { collectEventCategories } from "../state/event-categories";
import {
  bookedByAnother,
  eventFromPriceCents,
  eventSeatState,
  salesClosed,
  seatsInCart,
  ticketsInCart,
} from "../state/event-booking-rules";
import { useNow } from "../state/use-now";
import { EventBadges, SetMenuBlock, eventHeroUrl } from "./events/EventParts";
import { describeReason, specialDateOverrunNotice } from "@vertu/shared";
import { formatHm, tenantTz } from "../state/tenant-time";
import {
  menuLinesFromSplit,
  offersMenuChoice,
  describePartySnap,
  snapPartyToSpecialDate,
  specialDateAddBlocker,
  specialDateTotalCents,
  splitTotal,
  staleMenuRefusal,
  type GuestMenuSplit,
} from "../state/special-date";
import { WaitlistPrompt } from "./WaitlistPrompt";
import { useServiceBusyIntervals } from "../state/conflicts";
import { hoursPillText, useServiceHours } from "../state/service-hours";
import {
  displayUnitPrice,
  optionPriceSuffix as optionPriceSuffixFor,
  resolvedUnitPrice,
  stayTotalForUnit,
  unitPricingBasis,
  useUnitWindowPrices,
} from "../state/unit-pricing";
import {
  buildUnitCartLine,
  coversInCartFor,
  holdServiceUnit,
  removeUnitLines,
  unitCartCount as unitCartCountFor,
} from "../state/unit-cart";
import { useTransientFeedback } from "../state/feedback";
import { UnitCard } from "./booking/UnitCard";
import { guestNoun, partyMinFor } from "../state/option-led";
import { RoomGalleryModal } from "./booking/RoomGalleryModal";
import { HoldErrorBanner, useHoldFailure } from "./booking/HoldFailure";
import {
  SeededDateConfirmModal,
  useSeededDateConfirm,
} from "./booking/SeededDateConfirm";
import {
  ContinuingBanner,
  ServiceHero,
  renderInCartSummary,
} from "./booking/ServiceHero";

// Tabled services (dining/accommodation/anything with plans) expose two
// customer-facing modes: "general" = any available table, "seat" = pick on
// the floor plan. They never show a numbered unit list (customers don't
// know table numbers). Non-tabled services (events, tastings, spa, etc.)
// still render a list of ticket tiers / packages.
type PickerMode = "list" | "seat" | "general";

function isTabledService(s: WidgetService): boolean {
  if (s.floor_plans.length > 0) return true;
  // Dining without a floor plan still uses the general "any available table"
  // flow. Accommodation without a floor plan is a ROOM-TYPE picker: guests
  // choose a room type (with its photo, description and rate) from the unit
  // list — not a table — so it renders in list mode, never general/seat.
  return s.service_type === "dining";
}

/**
 * Renders an "available soon — please contact venue" panel when a
 * service can't be booked because its capacity hasn't been configured.
 * Shares the same hero shell as ServicePanel so the layout stays
 * consistent — only the body differs.
 */
function ServiceUnavailable({
  service,
  activeBrand,
  heroBrand,
  heroStyle,
  panelStyle,
}: {
  service: WidgetService;
  activeBrand: WidgetBrand | null;
  heroBrand?: WidgetBrand | null;
  heroStyle?: CSSProperties;
  panelStyle?: CSSProperties;
}) {
  const heroImage =
    service.hero_image_url ?? (heroBrand ?? activeBrand)?.logo_url ?? null;
  return (
    <section className="vt-panel" style={panelStyle}>
      <div className="vt-service-card">
        <div className="vt-hero" style={heroStyle}>
          {heroImage ? (
            <img
              src={optimizedImageUrl(heroImage, { width: 1280 })}
              srcSet={optimizedSrcSet(heroImage, IMG_WIDTHS.HERO)}
              sizes="100vw"
              alt={service.name}
              decoding="async"
            />
          ) : (
            <div className={`vt-hero-gradient vt-hero-${service.service_type}`} />
          )}
          <div className="vt-hero-overlay">
            <div className="vt-hero-overlay-text">
              <h2>{service.name}</h2>
              {service.tagline && <p>{service.tagline}</p>}
            </div>
          </div>
        </div>
        <div className="vt-service-body">
          <div className="vt-service-unavailable" role="status">
            <div className="vt-service-unavailable-title">
              Online bookings aren&apos;t open yet
            </div>
            <p className="vt-service-unavailable-body">
              This service is being set up. Please get in touch with the venue
              directly to enquire about availability.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function initialMode(s: WidgetService): PickerMode {
  if (isTabledService(s)) {
    if (s.allow_specific_reservation && s.floor_plans.length > 0) return "seat";
    // No floor plan → there are no tables to pick, so a dining service still
    // books via the general "seat on arrival" flow (never seat mode, which
    // would render an empty, unbookable panel).
    if (s.floor_plans.length === 0) return "general";
    if (s.allow_general_reservation) return "general";
    return "seat";
  }
  return "list";
}

export function ServicePanel({
  service,
  activeBrand,
  heroBrand,
  heroStyle,
  panelStyle,
  hostedEvents,
  onSelectHostedEvent,
  continuingFromServiceName,
  onDismissContinuing,
  onAddedToCart,
  onOpenCart,
  onEnquire,
  availabilityBadge,
  tenantTimezone,
}: {
  service: WidgetService;
  activeBrand: WidgetBrand | null;
  /** The service's own brand (hero image + colours in multi-brand widgets). */
  heroBrand?: WidgetBrand | null;
  /** Brand CSS vars scoped to the hero header (when tint reach = "header"). */
  heroStyle?: CSSProperties;
  /** Brand CSS vars scoped to the whole panel (when tint reach = "panel"). */
  panelStyle?: CSSProperties;
  /** Ticketed events hosted BY this service (e.g. "Pizza, Wine & Movies"). */
  hostedEvents?: WidgetEvent[];
  /** Open a hosted event's detail (jumps to the Events tab). */
  onSelectHostedEvent?: (eventId: string) => void;
  /**
   * When set, ServicePanel renders a gold "Continuing from X" banner
   * above the hero. Used by WidgetShell when the customer switched to
   * this service via a cross-sell panel — the banner carries context
   * forward so they know they're building up a multi-service visit.
   */
  continuingFromServiceName?: string | null;
  /**
   * Called when the customer dismisses the banner. WidgetShell clears
   * its continuingFrom state.
   */
  onDismissContinuing?: () => void;
  /** Open the cart drawer after an item is added, so the guest sees their order. */
  onAddedToCart?: () => void;
  /** Open the cart drawer on demand — the in-cart cards' "Checkout →" button. */
  onOpenCart?: () => void;
  /** Jump to the Enquire tab (event-only venues offer this for private events). */
  onEnquire?: () => void;
  /**
   * Tenant-configured capacity pill. `shown: false` hides the remaining-
   * capacity pills entirely; `label` templates the count fragment ("{count}"
   * is replaced with the remaining number — e.g. "Only {count} left"). The
   * " in area" / " in service" scope suffixes stay fixed. Absent (older
   * callers) → visible with the default "{count} left" template.
   */
  availabilityBadge?: { shown: boolean; label: string };
  /** The venue's IANA zone, for the special-date overrun line's clock. */
  tenantTimezone?: string | null;
}) {
  // Defense in depth — if the service somehow shipped without capacity
  // (e.g. an admin removed it after the widget was already published),
  // show an "unavailable" state instead of letting guests tap Book and
  // fail at create_booking_hold. See state/capacity.ts for the rules,
  // which mirror public.service_capacity_status from migration
  // 20260516000001_capacity_publish_gate.sql.
  if (!isServiceBookable(service)) {
    // A venue that takes no direct bookings but hosts ticketed events (e.g.
    // The Audrium: cinema / conferences / private screenings) should still
    // surface those events (bookable) plus an enquiry CTA, rather than
    // dead-ending at "online bookings aren't open". The venue panel renders
    // whenever there ARE events or the widget's Enquire tab is on — the CTA
    // must never disappear just because the last event has passed (that
    // window used to drop BOTH the events and the enquiry form at once).
    if ((hostedEvents && hostedEvents.length > 0 && onSelectHostedEvent) || onEnquire) {
      return (
        <EventVenuePanel
          service={service}
          activeBrand={activeBrand}
          heroBrand={heroBrand}
          heroStyle={heroStyle}
          panelStyle={panelStyle}
          events={hostedEvents ?? []}
          onSelectEvent={onSelectHostedEvent}
          onEnquire={onEnquire}
        />
      );
    }
    return (
      <ServiceUnavailable
        service={service}
        activeBrand={activeBrand}
        heroBrand={heroBrand}
        heroStyle={heroStyle}
        panelStyle={panelStyle}
      />
    );
  }
  const { add, remove, items: cartItems } = useCart();
  const linkPromo = useLinkPromoPreview();
  // Every add-to-cart path routes through this so the cart drawer opens
  // and the guest immediately sees the running state of their order.
  const addAndOpen: typeof add = (item) => {
    add(item);
    onAddedToCart?.();
  };
  // Cart-aware slot picking (state/conflicts.ts useServiceBusyIntervals).
  const busyIntervals = useServiceBusyIntervals(cartItems, service);

  // Service operating hours (state/service-hours.ts useServiceHours).
  const serviceHours = useServiceHours(service.id);

  const hasFloorPlans = service.floor_plans.length > 0;
  const tabledService = isTabledService(service);
  const showModeToggle =
    tabledService &&
    service.allow_general_reservation &&
    service.allow_specific_reservation &&
    hasFloorPlans;
  // Service types that surface a "Where would you like to sit?" picker
  // at booking time. Mirrors SEATING_AREA_APPLICABLE in the admin
  // applicability matrix (apps/admin/.../applicability.ts). Picker
  // itself renders null if the service has no seating_areas rows.
  const showSeatingPicker = ["dining", "tasting", "picnic"].includes(
    service.service_type,
  );
  // The datetime layout has a WHEN/DURATION grid we can drop the seating
  // <select> into (between WHEN and DURATION) with a preview below it.
  // Other layouts have no such row, so they keep the standalone chip
  // picker rendered after BookingSetup.
  const inlineSeating =
    showSeatingPicker &&
    (service.booking_panel_layout ?? "datetime") === "datetime";
  const seatingAreas = useSeatingAreas(inlineSeating ? service.id : null);

  const [mode, setMode] = useState<PickerMode>(() => initialMode(service));
  const [selectedUnitId, setSelectedUnitId] = useState<string | null>(
    // Don't auto-select a paid option when there are several — a guest could
    // otherwise book whatever happens to sort first (e.g. "Kiddies Pairing")
    // without noticing. Require an explicit choice; addDisabled below keeps the
    // Add button off until they pick. A single-option service still
    // auto-selects so there's nothing redundant to click.
    service.units.length === 1
      ? (service.units[0]?.id ?? null)
      : null,
  );
  // #351: list-mode multi-select. When a service offers SEVERAL non-physical
  // options (wine-tasting flights, tour variants), a party can tick more than
  // one with a per-option count instead of adding them one radio-pick at a
  // time — e.g. 2 guests on the MCC flight + 2 on the Red Wine flight. unitId →
  // count. Each ticked option becomes its own hold + cart line under ONE shared
  // cart_token, so the staff/admin surfaces read the split as a single visit.
  // Empty until the guest ticks something. selectedUnitId remains the
  // single-select state for the radio path (accommodation / physical units /
  // single-option services), which is unchanged.
  const [unitQty, setUnitQty] = useState<Record<string, number>>({});
  const [seatSelection, setSeatSelection] = useState<SeatSelection | null>(null);
  const [activePlanId, setActivePlanId] = useState<string | null>(
    service.floor_plans[0]?.id ?? null,
  );
  const [window, setWindow] = useState<BookingWindow | null>(null);
  // "Did you mean this date?" guard — components/booking/SeededDateConfirm.tsx.
  const {
    setSeededStartsAt,
    dateConfirm,
    setDateConfirm,
    requestDateConfirm: requestSeededDateConfirm,
  } = useSeededDateConfirm(JSON.stringify([service.id, window, selectedUnitId, unitQty]));
  const [qty, setQty] = useState(1);
  const { feedback, setFeedback } = useTransientFeedback();
  const { error, setError, errorEnquiryCta, reportHoldFailure: showHoldFailure } =
    useHoldFailure(onEnquire);
  const [stockRevision, setStockRevision] = useState(0);
  function reportHoldFailure(hold: { reason: string }) {
    // No menu chooser on screen yet the server wants menus: the page predates
    // the date's second menu. (menuChoice is read at call time, after render.)
    showHoldFailure(hold, staleMenuRefusal(hold.reason, menuChoice));
    // A refused hold is fresher evidence than the previous successful probe.
    // Refresh even when no line was added (the cart signature did not change).
    setStockRevision((revision) => revision + 1);
  }
  const [pending, setPending] = useState(false);
  // Per-card add in flight — ONLY the initiating card shows a busy state
  // (the hero/bottom CTAs keep their own `pending` flag for batch adds).
  const [pendingUnitId, setPendingUnitId] = useState<string | null>(null);
  // Root element — lets the card CTAs find and flash the .vt-setup-grid when
  // the guest hits Add before the reservation row is valid.
  const panelRef = useRef<HTMLElement | null>(null);
  const [unavailable, setUnavailable] = useState<Set<string>>(new Set());
  // Accommodation "View more" pop-up gallery — the unit whose photos are open.
  const [galleryUnit, setGalleryUnit] = useState<WidgetUnit | null>(null);
  // Live Cloudbeds availability for accommodation. Cloudbeds is the source of
  // truth across every channel/OTA, so we check it live rather than trust
  // Vertu's local inventory (avoids overselling). Null / non-Cloudbeds services
  // aren't filtered.
  const [roomAvailability, setRoomAvailability] =
    useState<RoomAvailability | null>(null);
  // Cheap signature of everything in the cart that could be holding stock.
  // Covers the WHOLE cart, not just this service: two services can share one
  // pool (a simulator bay and a lounge table, say), so another panel's line
  // can legitimately change this one's availability.
  const stockCartSig = useMemo(
    () =>
      cartItems
        .map((i) => `${i.id}:${i.serviceId}:${i.unitId ?? ""}:${i.quantity}`)
        .sort()
        .join("|"),
    [cartItems],
  );

  useEffect(() => {
    if (service.service_type !== "accommodation" || !window) {
      setRoomAvailability(null);
      return;
    }
    let cancelled = false;
    fetchRoomAvailability({ serviceId: service.id, window }).then((r) => {
      if (!cancelled) setRoomAvailability(r);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    service.service_type,
    service.id,
    window?.starts_at,
    window?.ends_at,
    window?.party_size,
  ]);
  // Drop a room selection that's no longer available for the picked dates —
  // both the single-select pick and any ticked multi-select rooms.
  useEffect(() => {
    const ra = roomAvailability;
    if (!ra?.cloudbeds || !ra.availableSlugs) return;
    const slugs = ra.availableSlugs;
    if (selectedUnitId) {
      const u = service.units.find((x) => x.id === selectedUnitId);
      if (u && !slugs.has(u.slug)) setSelectedUnitId(null);
    }
    setUnitQty((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const u of service.units) {
        if (next[u.id] && !slugs.has(u.slug)) {
          delete next[u.id];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [roomAvailability, selectedUnitId, service.units]);
  const [capacity, setCapacity] = useState<CapacityResponse | null>(null);
  // "N left in area · N left in service" — rendered inline next to the
  // hours pill in BookingSetup so the customer sees both signals at the
  // top of the form. Only set when the probe succeeded (ok=true) AND a
  // numeric remaining is known; capacity warnings still use the legacy
  // .vt-capacity-warning block below.
  // Hours pill for the HERO (moved out of the booking form per review —
  // status context belongs with the service name, not between the inputs).
  const heroHoursPill = useMemo(
    () =>
      hoursPillText(
        serviceHours,
        window ? new Date(window.starts_at) : new Date(),
      ),
    [serviceHours, window],
  );
  const capacityRemainingText = useMemo(() => {
    // Tenant-configurable pill: hidden outright, or a custom count template
    // ("{count}" → the remaining number). Scope suffixes stay fixed so the
    // area/service split keeps reading the same regardless of the template.
    const badge = availabilityBadge ?? { shown: true, label: "{count} left" };
    if (!badge.shown) return null;
    if (!capacity || capacity.ok !== true) return null;
    const frag = (n: number) => badge.label.replace("{count}", String(n));
    const bits: string[] = [];
    if (capacity.plan_remaining != null) {
      bits.push(`${frag(capacity.plan_remaining)} in area`);
    }
    if (capacity.service_remaining != null) {
      bits.push(`${frag(capacity.service_remaining)} in service`);
    }
    return bits.length > 0 ? bits.join(" · ") : null;
  }, [capacity, availabilityBadge]);
  // "Tastings from ZAR 75" — a hero pill that sits WITH the hours + capacity
  // chips on the left (previously a stray right-side block). Tasting services
  // only, and only when priced + prices aren't hidden.
  const heroPricePill = useMemo(() => {
    if (service.service_type !== "tasting") return null;
    if (service.show_prices_in_widget === false) return null;
    const priced = service.units.filter(
      (u) => typeof u.base_price === "number" && (u.base_price ?? 0) > 0,
    );
    if (priced.length === 0) return null;
    const cheapest = priced.reduce((min, u) =>
      (u.base_price ?? Infinity) < (min.base_price ?? Infinity) ? u : min,
    );
    const ccy = cheapest.currency ?? "ZAR";
    return `Tastings from ${ccy} ${Number(cheapest.base_price).toLocaleString()}`;
  }, [service.service_type, service.show_prices_in_widget, service.units]);
  // Dine-specific state (Stream 2.5 / 2.6 / 3.5). Harmless when the
  // service isn't dining — DineEnhancements only renders for services
  // that have seating_areas or service_subcategories configured.
  const [selectedSeatingAreaId, setSelectedSeatingAreaId] = useState<string | null>(null);
  const [selectedSeatingAreaName, setSelectedSeatingAreaName] = useState<string | null>(null);
  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState<string | null>(null);

  function handleAreaChange(id: string | null, name: string | null): void {
    setSelectedSeatingAreaId(id);
    setSelectedSeatingAreaName(name);
  }

  // Inline seating nodes for the datetime layout: the <select> goes into
  // the BookingSetup grid (between WHEN and DURATION), the hero + strip
  // render below it. Both null until the areas load / when there are none,
  // so the grid stays a clean 3-up until seating is actually available.
  const hasInlineSeating =
    inlineSeating && !!seatingAreas && seatingAreas.length > 0;
  const seatingField =
    hasInlineSeating && seatingAreas ? (
      <SeatingAreaField
        areas={seatingAreas}
        selectedAreaId={selectedSeatingAreaId}
        onAreaChange={handleAreaChange}
        label={fieldLabel(service, "seating", "Where")}
      />
    ) : null;
  const seatingPreview =
    hasInlineSeating && seatingAreas ? (
      <SeatingAreaPreview
        areas={seatingAreas}
        selectedAreaId={selectedSeatingAreaId}
      />
    ) : null;

  // When the service's booking_panel_layout is "scheduled_sessions",
  // the unit picker is OWNED by the BookingSetup tabs (which pin a
  // dated unit by writing its starts_at to the window). The inline
  // chip row + Quantity stepper are suppressed below, so the chip-
  // click state would never advance — derive selectedUnit from the
  // window instead so the cart submission and status pill stay in
  // sync with whichever session tab the customer picked.
  const isScheduledSessions =
    service.booking_panel_layout === "scheduled_sessions";
  // Per-person pricing: the guest (party) count IS the priced quantity, so the
  // party stepper drives the charge and the separate Quantity stepper is hidden
  // — same shape as scheduled sessions, just driven by the flag instead of the
  // layout. Undefined (older RPC) falls back to per-unit (historical decoupled
  // behaviour).
  const isPerPerson = (service.pricing_basis ?? "per_unit") === "per_person";
  // Per-OPTION pricing basis + card suffix (state/unit-pricing.ts).
  const unitBasis = (u: (typeof service.units)[number]) =>
    unitPricingBasis(service, u);
  const optionPriceSuffix = (u: (typeof service.units)[number]) =>
    optionPriceSuffixFor(service, u);
  // A per-person option's fewest guests per booking (units[].party_size_min;
  // 1 when none). The hold refuses fewer, so no count here starts below it.
  const unitMin = (u: WidgetUnit | undefined) => partyMinFor(service, u);
  const selectedUnit =
    isScheduledSessions
      ? window
        ? service.units.find((u) => u.starts_at === window.starts_at) ?? null
        : null
      : (service.units.find((u) => u.id === selectedUnitId) ?? null);
  const heroImage =
    service.hero_image_url ??
    (heroBrand ?? activeBrand)?.hero_image_url ??
    null;

  // Sessions AND per-person services price per-head, and BookingSetup's
  // PartyStepper owns the headcount. Keep qty == party_size so the cart line
  // math (qty × unit_price) reads correctly without exposing a duplicate
  // Quantity stepper to the customer.
  useEffect(() => {
    if (!isScheduledSessions && !isPerPerson) return;
    if (!window) return;
    if (qty === window.party_size) return;
    setQty(window.party_size);
  }, [isScheduledSessions, isPerPerson, window, qty]);

  // For events, the "selected event unit" is the seat's linked unit OR the
  // unit-picker value from list mode. BookingSetup uses it to drive the window.
  const selectedEventUnit: WidgetUnit | null =
    service.service_type === "events"
      ? seatSelection?.unit ?? selectedUnit
      : null;

  // Detect a special-date event overlap. If present, the service flips into
  // fixed-menu mode (Mother's Day etc.): pick a party size, view the set
  // menu, pay in full. Seat selection is optional — only shown when the
  // event pins to a specific floor plan.
  // Special dates with guest menus (20260925090010): how many guests take
  // each menu. Reset whenever the matched date changes; in fixed mode also
  // when the party chip changes (the split must add up to the new party).
  const [menuSplit, setMenuSplit] = useState<GuestMenuSplit>({});
  const matchedEvent = useMemo(() => {
    if (!window) return null;
    return matchSpecialDateEvent(
      service.special_date_events,
      window.starts_at,
      window.ends_at,
    );
  }, [service.special_date_events, window?.starts_at, window?.ends_at]);
  const matchedEventId = matchedEvent?.id ?? null;
  // The start-in rule (20261004090010): a window that runs past the matched
  // date's end is still that date's booking — say so under the banner, in
  // the venue's clock.
  const overrunNotice = useMemo(() => {
    const tz = tenantTz({ timezone: tenantTimezone });
    return specialDateOverrunNotice(matchedEvent, window?.ends_at, (iso) => formatHm(Date.parse(iso), tz));
  }, [matchedEvent, window?.ends_at, tenantTimezone]);
  useEffect(() => {
    setMenuSplit({});
  }, [matchedEventId]);
  const menuChoice = matchedEvent ? offersMenuChoice(matchedEvent) : false;
  // The date owns the party: move a seeded or carried-in party onto one the
  // date accepts (a chip, or into the range) so the CTA never quotes a party
  // the server refuses. Variable + menu choice is exempt — there the split
  // sets the party as the guest counts.
  const snappedParty =
    matchedEvent && window && !(menuChoice && matchedEvent.guest_selection_mode === "variable")
      ? snapPartyToSpecialDate(matchedEvent, window.party_size)
      : null;
  // The party the guest brought and the one the date set in its place, so
  // the move is announced and undone when the guest leaves for an ordinary
  // date. Kept only while the party is still the one the date set.
  const [partySnap, setPartySnap] = useState<{ own: number; snapped: number } | null>(null);
  useEffect(() => {
    if (snappedParty == null || !window || snappedParty === window.party_size) return;
    // Date to date: the guest's own party is still the one from before the
    // first move, unless they changed it in between.
    const own =
      partySnap && partySnap.snapped === window.party_size ? partySnap.own : window.party_size;
    setPartySnap(own === snappedParty ? null : { own, snapped: snappedParty });
    setWindow({ ...window, party_size: snappedParty });
    setSeatSelection(null);
    setMenuSplit({});
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs when the snap target changes
  }, [snappedParty, window?.party_size]);
  useEffect(() => {
    if (!partySnap || !window) return;
    // The guest chose a party themselves: theirs now, nothing to undo.
    if (window.party_size !== partySnap.snapped) {
      setPartySnap(null);
      return;
    }
    if (matchedEventId != null) return;
    // Back on an ordinary date: give the guest their own party back.
    setPartySnap(null);
    setWindow({ ...window, party_size: partySnap.own });
    setSeatSelection(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs when the date or party changes
  }, [matchedEventId, window?.party_size]);
  const partySnapNote =
    matchedEvent && snappedParty != null && partySnap && window?.party_size === partySnap.snapped
      ? describePartySnap(matchedEvent, partySnap.own, partySnap.snapped)
      : null;

  // Constrain the plans shown when a special-date event pins to one.
  const effectivePlanId = useMemo(() => {
    if (matchedEvent?.floor_plan_id) return matchedEvent.floor_plan_id;
    return activePlanId;
  }, [matchedEvent?.floor_plan_id, activePlanId]);

  // Units pinned to a physical seat on any plan shouldn't accept multiples —
  // Each unit on a dining/accommodation-style service (anything with floor
  // plans configured, or the types themselves) is a specific physical seat
  // — you can only book Table 2 or Suite 3 once per window, not four
  // times. Only GA tickets, packages, and non-spatial experiences keep
  // the quantity stepper.
  const serviceHasUniqueUnits = useMemo(() => {
    if (service.floor_plans.length > 0) return true;
    return ["accommodation", "dining"].includes(service.service_type);
  }, [service.floor_plans.length, service.service_type]);
  const selectedUnitIsPhysical =
    selectedUnit != null && serviceHasUniqueUnits;

  // #351: the option grid becomes CHECKBOX multi-select when a PER-PERSON
  // service offers more than one NON-physical option in the default datetime
  // list layout — the wine-tasting "split a party across flights" case, where
  // each option's count is a headcount priced per head.
  //
  // Multi-select = tick several options, each with its own quantity. Enabled
  // for any list + datetime service with >1 option, because per-option pricing
  // now makes it price correctly for BOTH bases:
  //   • per_person option — the count is that flight's headcount → one hold with
  //     party_size = count, priced per head.
  //   • per_item option — the count is the item quantity → N holds of one each
  //     (create_booking_hold reads the option's own pricing_basis via
  //     coalesce(unit, service), so each per_item hold charges base × 1).
  //   In both the shown total == the amount charged and capacity reserves right.
  //   • datetime layout only — the shared Party stepper is hidden only for the
  //     datetime layout; other layouts (time_list/single_slot/calendar_first)
  //     would show a duplicate, frozen stepper.
  // Still excludes single-option non-accommodation services, seat/general/
  // tabled modes, special-date fixed menus, scheduled_sessions and events.
  // Anything NOT matching stays the single-select radio path.
  const isAccommodation = service.service_type === "accommodation";
  // Accommodation is ALSO multi-select: a stay needs several room types +
  // quantities picked at once (5 guests, 1 night → 2× Deluxe + 1× Luxury) and
  // added to the cart in one go. Rooms are "unique unit" physical inventory, so
  // they'd fail the generic guard below — allow them explicitly. Each room type
  // gets a quantity ("rooms") stepper; the add loop holds `qty` rooms of each
  // (per-unit → base × nights each).
  const isMultiSelect =
    !isScheduledSessions &&
    !matchedEvent &&
    ((isAccommodation && service.units.length >= 1) ||
      (mode === "list" &&
        (service.booking_panel_layout ?? "datetime") === "datetime" &&
        !tabledService &&
        !serviceHasUniqueUnits &&
        service.service_type !== "events" &&
        service.units.length > 1));
  // Whole nights in the stay window — drives the accommodation price display so
  // the pill total matches what the server charges (base × rooms × nights).
  const stayNights =
    isAccommodation && window
      ? Math.max(
          1,
          Math.round(
            (Date.parse(window.ends_at) - Date.parse(window.starts_at)) /
              86400000,
          ),
        )
      : 1;

  // Seasonal DISPLAY pricing — resolve_unit_price per option for the chosen
  // window (state/unit-pricing.ts).
  const priceInputs = { units: service.units, window, showPrices: service.show_prices_in_widget };
  const unitPrices = useUnitWindowPrices(priceInputs);

  // Window-resolved option prices — UNROUNDED for storage, rounded for display,
  // exact calendar sums for stay totals. See state/unit-pricing.ts.
  const unitResolvedPrice = (u: WidgetUnit): number =>
    resolvedUnitPrice(unitPrices, u);
  const unitDisplayPrice = (u: WidgetUnit): number =>
    displayUnitPrice(unitPrices, u);
  const unitStayTotal = (u: WidgetUnit): number =>
    stayTotalForUnit(unitPrices, u, stayNights);

  // Products-optional: a list-mode service the tenant marked "allow general
  // reservation" lets a guest reserve the slot WITHOUT picking an option — a
  // deposit-only hold (per-guest deposit via resolve_deposit_cents), the choice
  // + balance settled at the venue. Mirrors the dining general-reservation flow.
  // NEVER for accommodation. A stay must carry a service_unit_id — the DB
  // guard (_bookings_guard_unpriced_accommodation) rejects a roomless hold,
  // because one previously priced to R0 and confirmed with no inventory.
  // With allow_general_reservation on, this branch rendered a "Reserve for N
  // guests" CTA that called create_booking_hold with serviceUnitId: null and
  // therefore failed 100% of the time. Four real guests hit it on 2026-08-03
  // and one abandoned. Accommodation falls through to the room picker, which
  // passes a real unit; "no exact room" is served by the enquiry CTA.
  const allowNoOption =
    mode === "list" &&
    service.allow_general_reservation === true &&
    !isAccommodation;

  // Per-card Add/Remove CTAs — quantity + action live ON each option card so
  // the guest never hunts for a separate button ("it gets lost" complaint).
  // Applies wherever the option grid renders EXCEPT events: event units are
  // dated ticket tiers whose booking window derives FROM the selected unit
  // (BookingSetup seeds it), so they keep the pick-then-CTA flow — a card CTA
  // could otherwise hold against a stale window.
  const hasCardCtas =
    mode === "list" &&
    !tabledService &&
    !isScheduledSessions &&
    !matchedEvent &&
    service.service_type !== "events" &&
    service.units.length > 0;

  // Units whose ATTRIBUTED STOCK is gone for the picked window (Booking V3).
  // Cloudbeds above only ever covers accommodation, so before this a unit
  // backed by real stock — a bike frame, a horse — rendered bookable right up
  // to the hold, and the guest was refused after filling in their details.
  // A set of unit ids that cannot be held; empty means nothing is gated.
  const [stockBlocked, setStockBlocked] = useState<Set<string>>(new Set());
  // What each card is probed at. A per-person option is asked "is there any
  // stock for this at all in the window?" — count 1 — never the count the
  // guest typed. A probe at the typed count greys the card and the reconcile
  // effect below erases the count, and once erased nothing re-probes to
  // un-grey it (port gate D1). So greying means sold out, full stop; a count
  // the stock cannot take is refused at Add with a message and kept. Other
  // options are probed at the party size, as before.
  const stockCountFor = (unit: WidgetUnit): number =>
    (hasCardCtas || isMultiSelect) && unitBasis(unit) === "per_person"
      ? 1
      : window?.party_size ?? 1;
  const isUnitUnavailable = (unit: WidgetUnit): boolean =>
    stockBlocked.has(unit.id) ||
    (!!roomAvailability?.cloudbeds &&
      !!roomAvailability.availableSlugs &&
      !roomAvailability.availableSlugs.has(unit.slug));
  useEffect(() => {
    // Called for EVERY service with units, not just stocked ones: the RPC
    // early-returns on one index probe when the service has no requirement
    // row, and reports every unit available, which is exactly what this
    // component already assumed. So it can only ever remove an option the
    // hold was going to refuse anyway.
    if (!service.units.length || !window?.starts_at || !window?.ends_at) {
      setStockBlocked(new Set());
      setUnitAttrs({});
      return;
    }
    let cancelled = false;
    type StockRow = {
      unit_id: string;
      is_available: boolean;
      attributes: Record<string, string> | null;
    };
    const probe = (endsAt: string, party: number) =>
      supabase.rpc("get_service_unit_stock_availability", {
        p_service_id: service.id,
        p_starts_at: window.starts_at,
        p_ends_at: endsAt,
        p_party_size: party,
      });
    // Fail OPEN. A read that did not answer must not grey out the grid —
    // the write path is the authority on whether a hold can be taken.
    const failOpen = () => {
      const none = new Set<string>();
      setStockBlocked(none);
      setUnitAttrs({});
      return none;
    };
    const apply = (rows: StockRow[]) => {
      const blocked = new Set(
        rows.filter((r) => r.is_available === false).map((r) => r.unit_id),
      );
      setStockBlocked(blocked);
      setUnitAttrs(
        Object.fromEntries(rows.map((r) => [r.unit_id, r.attributes ?? {}])),
      );
      return blocked;
    };
    if (!hasCardCtas && !isMultiSelect) {
      probe(window.ends_at, window.party_size ?? 1).then(({ data, error }) => {
        if (cancelled) return;
        if (error || !Array.isArray(data)) return failOpen();
        apply(data as StockRow[]);
      });
    } else {
      // Each option is checked on its own — never as "can the whole party do
      // every option?", which refused a party of 6 wanting 2 on one option —
      // and a per-person option at count 1 (see stockCountFor).
      const party = stockCountFor;
      Promise.all(
        service.units.map(async (u) => {
          const { data, error } = await probe(window.ends_at, party(u));
          if (error || !Array.isArray(data)) return null;
          return (data as StockRow[]).find((r) => r.unit_id === u.id) ?? null;
        }),
      ).then((rows) => {
        if (cancelled) return;
        if (rows.some((r) => r === null)) failOpen();
        else apply(rows as StockRow[]);
      });
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    service.id,
    service.units.length,
    window?.starts_at,
    window?.ends_at,
    window?.party_size,
    hasCardCtas,
    isMultiSelect,
    stockRevision,
    // NOT the ticked counts. A tick must not re-probe: option-led services
    // with the availability panel route there, whatever is left here renders
    // classic, and production never re-checked stock on a count change — a
    // re-probe here would let the reconcile effect below silently drop a
    // count the guest just typed. The hold is the authority: a count the
    // stock cannot take is refused at Add, with a message, and kept
    // (stockCountFor probes per-person options at 1, so a typed count can
    // never grey its own card). Port gate D1, v3-port-to-main-gates.
    // A cart line HOLDS stock — a pending hold is live inventory — so adding
    // one changes what is still bookable, including for OTHER services that
    // draw on the same pool. Without this the grid goes stale the moment the
    // guest adds anything: the card stays enabled, they press Add, and the
    // hold refuses. Signature rather than the array so this fires on a real
    // change and not on every render.
    stockCartSig,
  ]);
  // Each unit's stock attributes, from the same RPC as availability.
  const [unitAttrs, setUnitAttrs] = useState<
    Record<string, Record<string, string>>
  >({});
  // Which attribute values the guest has narrowed to. Empty = show everything.
  const [facetPicks, setFacetPicks] = useState<Record<string, string>>({});

  // The chip rows: every attribute key the units actually differ on. A key
  // every unit shares one value for is not a choice, so it is not offered —
  // that keeps "pedals: flat" off the screen for a fleet that is all flat.
  const facetKeys = useMemo(() => {
    const byKey = new Map<string, Set<string>>();
    for (const u of service.units) {
      for (const [k, v] of Object.entries(unitAttrs[u.id] ?? {})) {
        if (typeof v !== "string") continue;
        if (!byKey.has(k)) byKey.set(k, new Set());
        byKey.get(k)!.add(v);
      }
    }
    return [...byKey.entries()]
      .filter(([, vals]) => vals.size > 1)
      .map(([key, vals]) => ({
        key,
        // "frame_size" reads as "Frame size" to a guest.
        label: key.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase()),
        values: [...vals].sort(),
      }))
      .sort((a, b) => a.key.localeCompare(b.key));
  }, [service.units, unitAttrs]);

  // The grid, narrowed by the chips. With no chips picked this is every unit,
  // so a service without attributed stock renders exactly as it does today.
  const visibleUnits = useMemo(() => {
    const picks = Object.entries(facetPicks);
    if (!picks.length) return service.units;
    return service.units.filter((u) => {
      const a = unitAttrs[u.id];
      if (!a) return false;
      return picks.every(([k, v]) => a[k] === v);
    });
  }, [service.units, unitAttrs, facetPicks]);

  // Drop a pick whose stock has just gone — the same courtesy the Cloudbeds
  // effect below does for rooms. Without this the guest keeps a greyed-out
  // card selected and the Book button carries a unit the hold will refuse.
  useEffect(() => {
    if (!stockBlocked.size) return;
    if (selectedUnitId && stockBlocked.has(selectedUnitId)) {
      setSelectedUnitId(null);
    }
    setUnitQty((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const id of Object.keys(next)) {
        if (stockBlocked.has(id)) {
          delete next[id];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stockBlocked]);

  // The options the guest has ticked (count > 0). Each becomes one hold + one
  // cart line on Add. Only meaningful in multi-select mode.
  const tickedUnits = useMemo(
    () =>
      isMultiSelect
        ? service.units.filter((u) => (unitQty[u.id] ?? 0) > 0)
        : [],
    [isMultiSelect, service.units, unitQty],
  );
  // Sum of the per-option counts. For per_person this is the whole party's
  // headcount (each option's count = guests on that flight); for per_unit it's
  // the total item count. Drives the synced party_size + the status total.
  const multiCountTotal = tickedUnits.reduce(
    (n, u) => n + (unitQty[u.id] ?? 0),
    0,
  );
  // Accommodation base_price is per night, so the whole-stay total multiplies
  // by nights; every other basis is a flat per-item/per-person price (× 1).
  const multiPriceTotal = tickedUnits.reduce(
    (sum, u) => sum + unitStayTotal(u) * (unitQty[u.id] ?? 0),
    0,
  );
  // Only per_person options count as "guests" — a per_item quantity is an item
  // count (3 platters), not headcount, so it must never trip the party warning.
  const multiHeadcount = tickedUnits.reduce(
    (n, u) => n + (unitBasis(u) === "per_person" ? (unitQty[u.id] ?? 0) : 0),
    0,
  );
  const multiCurrency = tickedUnits[0]?.currency ?? "ZAR";
  // Accommodation multi-select: total beds across the picked rooms × quantity.
  // If it can't sleep the party we nudge (the single-select roomsNeeded warning
  // can't fire here — there's no single selectedUnit in multi-select).
  const multiRoomCapacity = tickedUnits.reduce(
    (n, u) => n + (u.capacity ?? 0) * (unitQty[u.id] ?? 0),
    0,
  );
  const roomsUnderCapacity =
    isMultiSelect &&
    isAccommodation &&
    window != null &&
    multiRoomCapacity > 0 &&
    multiRoomCapacity < window.party_size;

  // Tick / untick an option (a freshly-ticked option defaults to 1, or to
  // its minimum — "2–4 riders" starts at 2).
  const toggleUnit = (id: string) =>
    setUnitQty((prev) => {
      const next = { ...prev };
      if ((next[id] ?? 0) > 0) delete next[id];
      else next[id] = unitMin(service.units.find((u) => u.id === id));
      return next;
    });
  const setUnitQtyFor = (id: string, n: number) =>
    setUnitQty((prev) => {
      const next = { ...prev };
      if (n <= 0) delete next[id];
      else next[id] = n;
      return next;
    });

  // Reset the ticked options whenever the service changes.
  useEffect(() => {
    setUnitQty({});
  }, [service.id]);

  // The party stepper owns the visit's guest count (# people), INDEPENDENT of
  // the per-option quantities — a group of 4 might split 2 + 2, or a couple
  // might pick 3 tastings between them. When the total tastings picked exceeds
  // the guest count we WARN but still allow it (someone doing more than one).
  const multiOverPicked =
    isMultiSelect &&
    window != null &&
    multiHeadcount > window.party_size;

  // Historically gated the standalone action-row quantity stepper; that UI
  // moved onto the option cards (per-card stepper + Add), so this now only
  // gates handleAdd's hold-split math — split into N holds ONLY for flows
  // where a quantity stepper was legitimate: per-unit list services, never
  // seat/general mode, scheduled sessions or per-person services (party size
  // owns the count in both), physical unique units (always qty 1), or
  // multi-select services (per-option counts replace it). With the UI gone,
  // qty stays 1 on these paths, but the guard keeps the charge honest if a
  // future path sets qty again.
  const showQuantityStepper =
    mode !== "seat" &&
    mode !== "general" &&
    !tabledService &&
    service.units.length > 0 &&
    !isScheduledSessions &&
    !isPerPerson &&
    !isMultiSelect &&
    !selectedUnitIsPhysical;

  // Force qty = 1 the moment a physical unit becomes selected.
  useEffect(() => {
    if (selectedUnitIsPhysical && qty !== 1) setQty(1);
  }, [selectedUnitIsPhysical, qty]);

  // Accommodation: when the party won't fit the selected room type, work out
  // how many of that room they'd need so we can nudge them to add more rooms
  // (or pick a bigger type) instead of silently over-booking one room.
  const roomsNeeded =
    service.service_type === "accommodation" &&
    selectedUnit?.capacity != null &&
    selectedUnit.capacity > 0 &&
    window?.party_size != null &&
    window.party_size > selectedUnit.capacity
      ? Math.ceil(window.party_size / selectedUnit.capacity)
      : null;

  // Pull taken seats whenever the window, plan, or service changes.
  useEffect(() => {
    if (!window || !activePlanId) {
      setUnavailable(new Set());
      return;
    }
    let cancelled = false;
    fetchSeatAvailability({
      floorPlanId: activePlanId,
      window,
    }).then((set) => {
      if (!cancelled) setUnavailable(set);
    });
    return () => {
      cancelled = true;
    };
  }, [window?.starts_at, window?.ends_at, activePlanId, service.id]);

  // If the current selection becomes unavailable (window changed), drop it.
  useEffect(() => {
    if (seatSelection && unavailable.has(seatSelection.element.id)) {
      setSeatSelection(null);
      setCapacity(null);
    }
  }, [unavailable, seatSelection?.element.id]);

  // Preflight capacity when a seat is selected OR when the customer is in
  // general mode (no specific seat — just check service capacity).
  useEffect(() => {
    if (!window) {
      setCapacity(null);
      return;
    }
    if (mode === "seat" && !seatSelection) {
      setCapacity(null);
      return;
    }
    let cancelled = false;
    checkCapacity({
      serviceId: service.id,
      floorPlanId: mode === "seat" ? seatSelection?.plan.id ?? null : null,
      elementId: mode === "seat" ? seatSelection?.element.id ?? null : null,
      window,
    }).then((r) => {
      if (!cancelled) setCapacity(r);
    });
    return () => {
      cancelled = true;
    };
  }, [
    service.id,
    mode,
    seatSelection?.element.id,
    window?.starts_at,
    window?.ends_at,
    window?.party_size,
  ]);

  const effectiveUnit: WidgetUnit | null =
    mode === "seat" ? seatSelection?.unit ?? null : selectedUnit;

  // Why Add can't hold yet, or null when it can. The CTA then says so and a
  // press takes the guest to the fix (showAddBlock) instead of doing nothing.
  const addBlock = useMemo<AddBlock | null>(() => {
    const full = capacity && capacity.ok === false ? capacity.reason : null;
    if (matchedEvent) {
      if (!window) return "when";
      // Seat selection is only required when the event pins to a floor plan.
      if (matchedEvent.floor_plan_id && !seatSelection) return "seat";
      if (full !== null) return "capacity";
      // A date with a menu choice needs a menu for every guest first.
      if (specialDateAddBlocker(matchedEvent, window.party_size, menuSplit)) return "menus";
      return null;
    }
    if (mode === "seat") {
      if (!window) return "when";
      if (!seatSelection) return "seat";
      if (full !== null) return "capacity";
      return null;
    }
    if (mode === "general") {
      if (!window) return "when";
      if (full !== null) return "capacity";
      return null;
    }
    // #351 multi-select: enabled once a window is set AND at least one option
    // is ticked AND the service isn't already known-full (else every ticked
    // option round-trips to a failing hold).
    if (isMultiSelect) {
      if (!window) return "when";
      // allowNoOption: reserving without a pick is valid, so 0 ticked is OK.
      if (tickedUnits.length === 0 && !allowNoOption) return "options";
      if (full !== null) return "capacity";
      // Attributed stock (Booking V3). `capacity` above is the SERVICE-level
      // probe, and for unit-level stock that probe is optimistic BY DESIGN —
      // check_capacity_for_slot has no unit argument, so it answers "some unit
      // could serve this", not "this one can". Ticking an option whose own
      // stock is gone therefore left the CTA enabled and failed at the hold
      // with a bare `resource_unavailable`, which is exactly the round-trip
      // the comment above set out to avoid.
      if (tickedUnits.some((u) => stockBlocked.has(u.id))) return "stock";
      return null;
    }
    // list-mode (tastings, tours, picnics) — needs both a unit AND a
    // window. The window is what create_booking_hold reserves and what
    // the cart row carries forward to checkout, so without it we can't
    // hand off a complete booking. (allowNoOption lets a unit-less
    // reservation through — a deposit-only hold.)
    if (!window) return "when";
    if (service.units.length > 0 && !selectedUnit && !allowNoOption) return "options";
    // Same reason as the multi-select branch: the chosen unit's own stock, not
    // the service-level probe's optimistic answer.
    if (selectedUnit && stockBlocked.has(selectedUnit.id)) return "stock";
    return null;
  }, [matchedEvent, menuSplit, mode, seatSelection, window, capacity, selectedUnit, service.units.length, isMultiSelect, tickedUnits, allowNoOption, stockBlocked]);
  const addDisabled = addBlock !== null;

  // The seeded-date confirm (components/booking/SeededDateConfirm.tsx). Events +
  // scheduled sessions derive their own dates, so they're exempt.
  function requestDateConfirm(proceed: () => void): boolean {
    return requestSeededDateConfirm(proceed, {
      window,
      exempt:
        Boolean(matchedEvent) ||
        service.service_type === "events" ||
        isScheduledSessions,
    });
  }

  const latestAddRef = useRef<{ batch: () => Promise<void>; unit: (unit: WidgetUnit, count: number) => Promise<void> } | null>(null);
  async function handleAdd() {
    if (requestDateConfirm(() => { void latestAddRef.current?.batch(); })) return;
    setError(null);
    if (matchedEvent) {
      if (!window) return;
      setPending(true);
      try {
        const hold = await createBookingHold({
          serviceId: service.id,
          floorPlanId: seatSelection?.plan.id ?? matchedEvent.floor_plan_id ?? null,
          elementId: seatSelection?.element.id ?? null,
          serviceUnitId: seatSelection?.element.service_unit_id ?? null,
          seatingAreaId: selectedSeatingAreaId,
          window,
          guest: { name: null, email: null, phone: null },
          cartToken: getCartToken(),
          // Two or more guest menus: how many take each. The server prices
          // the hold from this and refuses a split that does not add up.
          menuLines: menuChoice ? menuLinesFromSplit(matchedEvent, menuSplit) : null,
        });
        stampVisitPartySize(window.party_size);
        if (!hold.ok) {
          reportHoldFailure(hold);
          return;
        }
        const label = seatSelection?.element.label ?? matchedEvent.name;
        addAndOpen({
          serviceId: service.id,
          serviceName: service.name,
          serviceType: service.service_type,
          unitId: seatSelection?.unit?.id ?? null,
          unitName: seatSelection?.unit?.name ?? matchedEvent.name,
          description: [service.name, matchedEvent.name, seatSelection?.element.label]
            .filter(Boolean)
            .join(" — "),
          quantity: 1,
          // The whole reservation's price as the server wrote it — Σ of the
          // menu split when there is one, else price × party. (The line used
          // to carry ONE guest's price with quantity 1, under-reading every
          // special date beside the full-amount policy chip.)
          unitPrice: hold.amount_cents / 100,
          currency: matchedEvent.currency,
          brandId: service.brand_id,
          brandName: activeBrand?.name ?? null,
          metadata: {
            booking_id: hold.booking_id,
            booking_reference: hold.reference ?? null,
            starts_at: window.starts_at,
            ends_at: window.ends_at,
            party_size: window.party_size,
            amount_cents: hold.amount_cents,
            requires_full_payment: hold.requires_full_payment,
            special_date_event_id: hold.special_date_event_id,
            special_date_event_name: matchedEvent.name,
            special_date_menu_lines: hold.special_date_menu_lines ?? [],
            seat_label: seatSelection?.element.label ?? null,
            floor_plan_id: seatSelection?.plan.id ?? matchedEvent.floor_plan_id ?? null,
            floor_plan_name: seatSelection?.plan.name ?? null,
          },
        });
        setSeatSelection(null);
        setCapacity(null);
        setMenuSplit({});
        if (seatSelection?.element.id) {
          setUnavailable((prev) => new Set([...prev, seatSelection.element.id]));
        }
        setFeedback(`${label} added — pay to confirm`);
        setTimeout(() => setFeedback(null), 2500);
      } finally {
        setPending(false);
      }
      return;
    }
    if (mode === "general") {
      if (!window) return;
      setPending(true);
      try {
        const hold = await createBookingHold({
          serviceId: service.id,
          floorPlanId: null,
          elementId: null,
          serviceUnitId: null,
          seatingAreaId: selectedSeatingAreaId,
          window,
          guest: { name: null, email: null, phone: null },
          cartToken: getCartToken(),
        });
        stampVisitPartySize(window.party_size);
        if (!hold.ok) {
          reportHoldFailure(hold);
          return;
        }
        addAndOpen({
          serviceId: service.id,
          serviceName: service.name,
          serviceType: service.service_type,
          unitId: null,
          unitName: null,
          description: `${service.name} — any available table`,
          quantity: 1,
          unitPrice: null,
          currency: "ZAR",
          brandId: service.brand_id,
          brandName: activeBrand?.name ?? null,
          metadata: {
            booking_id: hold.booking_id,
            booking_reference: hold.reference ?? null,
            starts_at: window.starts_at,
            ends_at: window.ends_at,
            party_size: window.party_size,
            amount_cents: hold.amount_cents,
            requires_full_payment: hold.requires_full_payment,
            general_reservation: true,
            seating_area_id: selectedSeatingAreaId,
            seating_area_name: selectedSeatingAreaName,
          },
        });
        setFeedback("Booking held — added to cart");
        setTimeout(() => setFeedback(null), 2500);
      } finally {
        setPending(false);
      }
      return;
    }

    if (mode === "seat") {
      if (!seatSelection || !window) return;
      setPending(true);
      try {
        const hold = await createBookingHold({
          serviceId: service.id,
          floorPlanId: seatSelection.plan.id,
          elementId: seatSelection.element.id,
          serviceUnitId: seatSelection.element.service_unit_id,
          seatingAreaId: selectedSeatingAreaId,
          window,
          guest: { name: null, email: null, phone: null },
          cartToken: getCartToken(),
        });
        stampVisitPartySize(window.party_size);
        if (!hold.ok) {
          reportHoldFailure(hold);
          return;
        }
        const { element, plan } = seatSelection;
        const unit = seatSelection.unit;
        addAndOpen({
          serviceId: service.id,
          serviceName: service.name,
          serviceType: service.service_type,
          unitId: unit?.id ?? null,
          unitName: unit?.name ?? null,
          description: [service.name, plan.name, element.label ?? unit?.name]
            .filter(Boolean)
            .join(" — "),
          quantity: 1,
          unitPrice: unit ? unitResolvedPrice(unit) || null : null,
          currency: unit?.currency ?? "ZAR",
          brandId: service.brand_id,
          brandName: activeBrand?.name ?? null,
          metadata: {
            booking_id: hold.booking_id,
            booking_reference: hold.reference ?? null,
            floor_plan_id: plan.id,
            floor_plan_name: plan.name,
            seat_element_id: element.id,
            seat_label: element.label,
            starts_at: window.starts_at,
            ends_at: window.ends_at,
            party_size: window.party_size,
            amount_cents: hold.amount_cents,
            requires_full_payment: hold.requires_full_payment,
            special_date_event_id: hold.special_date_event_id,
            seating_area_id: selectedSeatingAreaId,
            seating_area_name: selectedSeatingAreaName,
          },
        });
        setSeatSelection(null);
        setCapacity(null);
        setUnavailable((prev) => new Set([...prev, element.id]));
        setFeedback("Seat held — added to cart");
        setTimeout(() => setFeedback(null), 2500);
      } finally {
        setPending(false);
      }
      return;
    }

    // #351: list-mode multi-select — one hold + one cart line per ticked
    // option, all under ONE cart_token (getCartToken() is cached, so every
    // hold + line lands under the same token) → the staff/admin surfaces read
    // the split as a single visit. Each line is stamped from its OWN hold
    // response (never reuse the first hold's booking_id / amount_cents). Holds
    // run sequentially — parallel holds would race capacity for the same
    // window/service and muddy partial-failure handling.
    // Products-optional: nothing picked but the service allows a plain
    // reservation → a deposit-only hold with NO unit, mirroring the dining
    // general-reservation flow. The choice + balance settle at the venue.
    if (
      allowNoOption &&
      (isMultiSelect ? tickedUnits.length === 0 : !selectedUnit)
    ) {
      if (!window) return;
      setPending(true);
      try {
        const hold = await createBookingHold({
          serviceId: service.id,
          floorPlanId: null,
          elementId: null,
          serviceUnitId: null,
          seatingAreaId: null,
          window,
          guest: { name: null, email: null, phone: null },
          cartToken: getCartToken(),
        });
        stampVisitPartySize(window.party_size);
        if (!hold.ok) {
          reportHoldFailure(hold);
          return;
        }
        addAndOpen({
          serviceId: service.id,
          serviceName: service.name,
          serviceType: service.service_type,
          unitId: null,
          unitName: null,
          description: `${service.name} — reservation`,
          quantity: 1,
          unitPrice: null,
          currency: "ZAR",
          brandId: service.brand_id,
          brandName: activeBrand?.name ?? null,
          metadata: {
            booking_id: hold.booking_id,
            booking_reference: hold.reference ?? null,
            starts_at: window.starts_at,
            ends_at: window.ends_at,
            party_size: window.party_size,
            amount_cents: hold.amount_cents,
            requires_full_payment: hold.requires_full_payment,
            general_reservation: true,
          },
        });
        setFeedback("Reservation held — added to cart");
        setTimeout(() => setFeedback(null), 2500);
      } finally {
        setPending(false);
      }
      return;
    }
    if (isMultiSelect) {
      if (!window) return;
      const lines = tickedUnits
        .map((u) => ({ u, n: unitQty[u.id] ?? 0 }))
        .filter((x) => x.n > 0)
        .map((x) => ({ ...x, n: Math.max(x.n, unitMin(x.u)) }));
      if (lines.length === 0) return;
      setPending(true);
      // Options FULLY secured — untick on any later failure so a retry can't
      // double-add them.
      const doneUnitIds: string[] = [];
      // Seated covers to reserve ONCE for the whole visit, beyond what the
      // per_person flights already reserve (each of those holds its own head-
      // count). The first per_item hold carries this gap so the seated party is
      // always accounted for against service capacity; if there are no per_item
      // options it stays uncovered (the pre-existing per_person-only behaviour).
      const coverGap = Math.max(0, window.party_size - multiHeadcount);
      let seatedPlaced = false;
      const visitGuests = window.party_size;
      // Accommodation only: spread the party across the booked rooms by their
      // capacity (fill each to capacity until guests run out) so each room
      // booking carries a sensible occupancy for housekeeping / breakfast,
      // instead of piling the whole party onto one room.
      let guestsToSeat = window.party_size;
      try {
        for (const { u, n } of lines) {
          const basis = unitBasis(u);
          // per_person: n is the headcount on THIS option → ONE hold whose
          // party_size = n, priced per head (base × n). per_item: n is the item
          // count → N holds of one each; create_booking_hold reads the option's
          // own pricing_basis, so each per_item hold charges base × 1.
          const perPerson = basis === "per_person";
          const holdsForOption = perPerson ? 1 : n;
          for (let k = 0; k < holdsForOption; k++) {
            // per_person: the hold's party_size IS the headcount (drives the
            // per-head charge AND reserves that many covers).
            // accommodation: the room charge ignores party; assign each room the
            // next slice of the party (capped at its capacity) so occupancy is
            // sensible and the picked rooms sum to the guests they can seat.
            // per_item (other): the charge is base × 1 regardless of party. The
            // FIRST per_item hold carries the seated headcount the per_person
            // flights don't already cover (so 3 platters for a party of 6 still
            // reserves 6 covers and never overbooks a capacity-limited service);
            // every other per_item hold carries 1.
            let holdParty: number;
            if (perPerson) {
              holdParty = n;
            } else if (isAccommodation) {
              const cap =
                u.capacity && u.capacity > 0 ? u.capacity : Math.max(1, guestsToSeat);
              holdParty = Math.max(1, Math.min(cap, Math.max(1, guestsToSeat)));
              guestsToSeat = Math.max(0, guestsToSeat - holdParty);
            } else {
              holdParty = seatedPlaced ? 1 : Math.max(1, coverGap);
              seatedPlaced = true;
            }
            const w = { ...window, party_size: holdParty };
            const hold = await holdServiceUnit({
              serviceId: service.id,
              unitId: u.id,
              window: w,
              visitPartySize: visitGuests,
            });
            if (!hold.ok) {
              reportHoldFailure(hold);
              // Keep every hold already secured (they're real holds in the
              // cart). Untick options fully added; for a per_item option that
              // failed partway, leave only the REMAINDER ticked so a retry
              // tops it up instead of re-adding the ones we got.
              setUnitQty((prev) => {
                const next = { ...prev };
                for (const id of doneUnitIds) delete next[id];
                if (k > 0) next[u.id] = n - k;
                return next;
              });
              if (doneUnitIds.length || k > 0) onAddedToCart?.();
              return;
            }
            add(
              buildUnitCartLine({
                service,
                unit: u,
                hold,
                window: w,
                // per_person: one line for the whole flight (qty = heads). per_item:
                // one line per item (qty 1), so N ticked = N lines of base each.
                quantity: perPerson ? n : 1,
                unitPrice: unitResolvedPrice(u) || null,
                // The whole visit's guest count (# people) from the party
                // stepper, so the cart can group these split lines as one
                // "N-guest visit".
                visitGuests,
                pricingBasis: perPerson ? "per_person" : "per_unit",
                brandName: activeBrand?.name ?? null,
              }),
            );
          }
          doneUnitIds.push(u.id);
        }
        setUnitQty({});
        onAddedToCart?.();
        setFeedback("Booking held — added to cart");
        setTimeout(() => setFeedback(null), 2500);
      } finally {
        setPending(false);
      }
      return;
    }

    if (!selectedUnit && service.units.length > 0) return;
    // List-mode (tastings, tours, picnics, custom). The list branch
    // previously skipped createBookingHold + dropped window data on the
    // floor — cart rows ended up with no starts_at/ends_at/party_size,
    // breaking conflict detection, the operating-hours trigger, and
    // checkout. Now we hold capacity for the same starts_at/ends_at
    // window the customer picked in BookingSetup and stamp the cart
    // row metadata identically to the seat / general branches.
    if (!window) return;
    setPending(true);
    try {
      // Accommodation: the stepper is a ROOM count — reserve that many rooms,
      // each its own hold + cart line (nights are driven by the picked dates,
      // and the server prices each hold at nights × rate). Other per-unit
      // services keep the single hold with quantity = qty.
      // Revenue-leak fix: a per-unit single-option service showed a quantity
      // stepper but created ONE hold — qty 3 displayed as 3 × base while the
      // server charged base × 1 (checkout sums holds). Per-unit/per-item
      // quantities now hold N of one each (the same mechanism as accommodation
      // rooms and the multi-select per-item path), so charge == display.
      // Per-person stays a single hold — party_size drives its charge.
      // Review fix (x2): only split into N holds when the guest actually SAW a
      // quantity stepper (showQuantityStepper) — qty is silently synced to
      // party_size for scheduled-sessions/per-person flows, so splitting there
      // would mint party_size duplicate holds and overcharge for a quantity the
      // guest never chose. And spread covers like the multi-select path: the
      // FIRST hold carries the visit's party, the rest carry 1 — otherwise N
      // holds x full party over-reserves N x party covers against capacity.
      const holdsToCreate = isAccommodation
        ? Math.max(1, qty)
        : showQuantityStepper &&
            selectedUnit &&
            unitBasis(selectedUnit) !== "per_person"
          ? Math.max(1, qty)
          : 1;
      // unitResolvedPrice, not raw base_price: the multi-select and seat paths
      // already show the window-resolved rate; this path lagging on the flat
      // price meant the one-room case — most stays — put the stale number in
      // the cart sub-line.
      const price = selectedUnit ? unitResolvedPrice(selectedUnit) || null : null;
      let added = 0;
      for (let i = 0; i < holdsToCreate; i++) {
        const w =
          holdsToCreate > 1 && i > 0 ? { ...window, party_size: 1 } : window;
        const hold = await holdServiceUnit({
          serviceId: service.id,
          unitId: selectedUnit?.id ?? null,
          window: w,
          visitPartySize: window.party_size,
        });
        if (!hold.ok) {
          // First room can't be held → surface the reason. A later room
          // failing means inventory ran out mid-add: keep what we secured.
          if (added === 0) {
            reportHoldFailure(hold);
            return;
          }
          break;
        }
        addAndOpen(
          buildUnitCartLine({
            service,
            unit: selectedUnit,
            hold,
            window: w,
            // One cart line per hold when we split into N holds (each priced
            // base × 1 server-side); a single per-person hold keeps qty on the line.
            quantity: holdsToCreate > 1 ? 1 : qty,
            unitPrice: price,
            // The whole visit's headcount rides in visit_guests for cart
            // grouping; the hold's own party is w.party_size (first hold carries
            // the visit's party, the rest carry 1).
            visitGuests: window.party_size,
            // Tells the cart whether editing the in-cart guests stepper should
            // also move the priced quantity (per_person) or leave it alone
            // (per_unit — the historical decoupled behaviour).
            pricingBasis: isPerPerson ? "per_person" : "per_unit",
            brandName: activeBrand?.name ?? null,
          }),
        );
        added++;
      }
      setFeedback(
        holdsToCreate > 1 && added < holdsToCreate
          ? `Held ${added} of ${holdsToCreate} — the rest ${isAccommodation ? "aren’t available for these dates" : "couldn’t be held"}`
          : added > 1
            ? `${added}× held — added to cart`
            : "Booking held — added to cart",
      );
      setTimeout(() => setFeedback(null), 2800);
    } finally {
      setPending(false);
    }
  }

  // Card-CTA nudge: the guest hit Add before the reservation row was valid
  // (no window yet / capacity known-full). Instead of a silent no-op, scroll
  // the setup grid into view and flash it so the missing step is obvious.
  function nudgeSetup() {
    const grid = panelRef.current?.querySelector<HTMLElement>(".vt-setup-grid");
    if (!grid) return;
    grid.scrollIntoView({ behavior: "smooth", block: "center" });
    grid.classList.add("vt-setup-grid-flash");
    setTimeout(() => grid.classList.remove("vt-setup-grid-flash"), 2200);
  }

  // Covers already reserved for this service by in-cart lines. The first
  // per_item hold of a card add tops the seated party up to the window's
  // party_size (so 3 platters for a party of 6 still reserve 6 covers)
  // WITHOUT re-reserving covers that earlier card adds already hold.
  const coversInCart = coversInCartFor(cartItems, service.id);

  // Per-unit cart total for the cards' "In booking × N ✓" badges (combo lines
  // excluded — see state/unit-cart.ts).
  const unitCartCount = (unitId: string) =>
    unitCartCountFor(cartItems, service.id, unitId);

  // Per-card add: hold(s) for ONE option using the current window/party
  // context — the same shapes as handleAdd's multi-select loop, for a single
  // unit. per_person: the count is that option's headcount → ONE hold whose
  // party_size = count, priced per head. per_item / rooms: N holds of one
  // each (create_booking_hold reads the option's own pricing_basis, so each
  // charges base × 1) — never collapsed into one hold. A cart line is only
  // ever added AFTER its own successful hold — checkout charges every hold
  // under the cart_token, so a line without a hold (or vice versa) breaks
  // the money path.
  async function addUnitToCart(unit: WidgetUnit, count: number) {
    if (!window || pendingUnitId || pending) return;
    if (isUnitUnavailable(unit)) return;
    if (requestDateConfirm(() => { void latestAddRef.current?.unit(unit, count); })) return;
    setError(null);
    setPendingUnitId(unit.id);
    const perPerson = unitBasis(unit) === "per_person";
    const n = Math.max(unitMin(unit), count);
    const visitGuests = window.party_size;
    const holdsForOption = perPerson ? 1 : n;
    const coverGap = Math.max(0, window.party_size - coversInCart);
    let seatedPlaced = false;
    // Accommodation: spread the party across the added rooms by capacity
    // (mirrors the multi-select loop) so occupancy reads sensibly.
    let guestsToSeat = Math.max(0, window.party_size - coversInCart);
    let added = 0;
    try {
      for (let k = 0; k < holdsForOption; k++) {
        let holdParty: number;
        if (perPerson) {
          holdParty = n;
        } else if (isAccommodation) {
          const cap =
            unit.capacity && unit.capacity > 0
              ? unit.capacity
              : Math.max(1, guestsToSeat);
          holdParty = Math.max(1, Math.min(cap, Math.max(1, guestsToSeat)));
          guestsToSeat = Math.max(0, guestsToSeat - holdParty);
        } else {
          holdParty = seatedPlaced ? 1 : Math.max(1, coverGap);
          seatedPlaced = true;
        }
        const w = { ...window, party_size: holdParty };
        const hold = await holdServiceUnit({
          serviceId: service.id,
          unitId: unit.id,
          window: w,
          visitPartySize: visitGuests,
        });
        if (!hold.ok) {
          reportHoldFailure(hold);
          // Keep every hold already secured (they're real cart lines). Leave
          // the REMAINDER pending on the card so a retry tops it up instead
          // of re-adding the ones we got.
          setUnitQtyFor(unit.id, n - k);
          if (added > 0) onAddedToCart?.();
          return;
        }
        add(
          buildUnitCartLine({
            service,
            unit,
            hold,
            window: w,
            // per_person: one line for the option (qty = heads). per_item: one
            // line per hold (qty 1), so N added = N lines of base each.
            quantity: perPerson ? n : 1,
            // unitResolvedPrice, not raw base_price — same reason as the
            // multi-select and seat paths above. This is the only add path
            // for a room, so the flat rate landing here put a rate the guest
            // is not being charged into every stay in the cart.
            unitPrice: unitResolvedPrice(unit) || null,
            visitGuests,
            pricingBasis: perPerson ? "per_person" : "per_unit",
            brandName: activeBrand?.name ?? null,
          }),
        );
        added++;
      }
      // Clear the card's pending count — the "In booking × N ✓" badge takes
      // over as the card's state from here.
      setUnitQtyFor(unit.id, 0);
      onAddedToCart?.();
      setFeedback(`${unit.name} held — added to cart`);
      setTimeout(() => setFeedback(null), 2500);
    } finally {
      setPendingUnitId(null);
    }
  }

  latestAddRef.current = { batch: handleAdd, unit: addUnitToCart };

  // Per-card remove: cancel every hold this option has in the cart, then drop
  // the lines (state/unit-cart.ts removeUnitLines).
  function removeUnitFromCart(unit: WidgetUnit) {
    removeUnitLines(cartItems, service.id, unit.id, remove);
  }

  // Shared so the booking CTA + capacity notice can appear at the TOP of the
  // panel (so a guest sees them without scrolling) AND in their usual spot at
  // the bottom of the action row.
  const ctaLabel =
    feedback ??
    (pending
      ? "Holding…"
      : addBlock
        ? addBlockLabel(addBlock)
        : matchedEvent && window
        ? buildFixedMenuCtaLabel(matchedEvent, window.party_size, seatSelection, menuSplit)
        : isMultiSelect
          ? tickedUnits.length === 0
            ? allowNoOption && window
              ? `Reserve for ${window.party_size} guest${window.party_size === 1 ? "" : "s"} →`
              : isAccommodation
                ? "Pick your rooms"
                : "Pick your options"
            : isAccommodation
              ? `Add ${multiCountTotal} room${multiCountTotal === 1 ? "" : "s"} to booking →`
              : `Add ${tickedUnits.length} to booking →`
          : !selectedUnit && allowNoOption && window
            ? `Reserve for ${window.party_size} guest${window.party_size === 1 ? "" : "s"} →`
            : buildCtaLabel(service, mode, effectiveUnit, qty, seatSelection, window));

  const capacityWarning =
    roomsNeeded != null && selectedUnit ? (
      <div className="vt-capacity-warning" role="status">
        <strong>{selectedUnit.name}</strong> sleeps up to{" "}
        {selectedUnit.capacity}. For {window?.party_size} guests you&rsquo;ll
        need about {roomsNeeded} rooms — add this room {roomsNeeded}×, mix in
        other room types, or pick a larger type to fit everyone.
      </div>
    ) : null;

  // What the CTA says while Add can't hold yet: the step that is missing.
  function addBlockLabel(block: AddBlock): string {
    switch (block) {
      case "when":
        return isAccommodation ? "Pick your dates first" : "Pick a date first";
      case "seat":
        return matchedEvent ? "Pick a seat on the plan above" : `Select a table on ${service.name}`;
      case "capacity":
        return capacityCtaLabel(capacity && capacity.ok === false ? capacity.reason : null, isAccommodation);
      case "menus":
        return (
          (matchedEvent && window && specialDateAddBlocker(matchedEvent, window.party_size, menuSplit)) ||
          "Choose a menu for every guest"
        );
      case "options":
        return isMultiSelect
          ? isAccommodation
            ? "Pick your rooms"
            : "Pick your options"
          : isAccommodation
            ? "Pick a room"
            : service.service_type === "tasting"
              ? "Pick a tasting"
              : service.service_type === "tour"
                ? "Pick a tour"
                : "Pick an option";
      case "stock":
        return "That option is taken: pick another";
    }
  }
  // A press on the CTA while Add can't hold: take the guest to the step that
  // is missing (lib/show-whats-missing.ts). It never adds anything.
  function showAddBlock() {
    const root = panelRef.current;
    if (!root || !addBlock) return;
    const q = (sel: string) => root.querySelector<HTMLElement>(sel);
    const seatTaken = capacity && capacity.ok === false && capacity.reason === "seat_taken";
    if (addBlock === "when" || (addBlock === "capacity" && !seatTaken)) {
      nudgeSetup();
    } else if (addBlock === "seat" || addBlock === "capacity") {
      showWhatsMissing(q(".vt-plan"));
    } else if (addBlock === "menus") {
      const split = q(".vt-menu-split") ?? q(".vt-fixed-party");
      showWhatsMissing(split, split?.querySelector<HTMLElement>("button:not(:disabled)"));
    } else {
      // Scheduled sessions pick theirs in the setup row: no cards.
      const cards = q(".vt-option-cards");
      if (!cards) {
        nudgeSetup();
        return;
      }
      showWhatsMissing(
        cards,
        cards.querySelector<HTMLElement>('.vt-option-card[tabindex="0"]:not([aria-disabled="true"])'),
      );
    }
  }
  const blocked = addBlock !== null && !pending && !feedback;
  // The capacity check says the time is full (not a failed check or another
  // refusal): the option cards say so too.
  const capacityFull =
    capacity != null && capacity.ok === false && FULL_REASONS.has(String(capacity.reason));

  // The "Add to booking" step is a SECOND click after selecting/qty — easy to
  // miss. The moment a valid selection exists (and we're not mid-add or showing
  // success), the CTA glows + gently pulses so it's unmistakably the next step.
  const ctaReady = !addDisabled && !pending && !feedback;
  // Fresh element per render site — the same CTA now appears both inline in
  // the reservation row (BookingSetup's ctaSlot) and in the bottom action
  // row, and one React element can't be mounted in two places. Never a dead
  // button: while Add can't hold it says why and a press shows the fix.
  const renderCta = () => (
    <button
      type="button"
      className={`vt-cta-inline${
        feedback
          ? " vt-cta-inline-success"
          : ctaReady
            ? " vt-cta-inline-ready"
            : blocked
              ? " vt-cta-blocked"
              : ""
      }`}
      onClick={addBlock ? showAddBlock : handleAdd}
      disabled={pending}
      aria-disabled={blocked || undefined}
    >
      {ctaLabel}
    </button>
  );
  const ctaButton = renderCta();

  // What this service already has in the cart — see renderInCartSummary.
  const inCartSummary = renderInCartSummary(cartItems, service.id, linkPromo);

  return (
    <section className="vt-panel" style={panelStyle} ref={panelRef}>
      {continuingFromServiceName && (
        <ContinuingBanner
          continuingFromServiceName={continuingFromServiceName}
          onDismissContinuing={onDismissContinuing}
        />
      )}
      <div className="vt-service-card">
      <ServiceHero
        service={service}
        heroImage={heroImage}
        heroStyle={heroStyle}
        heroHoursPill={heroHoursPill}
        capacityRemainingText={capacityRemainingText}
        heroPricePill={heroPricePill}
        cta={
          /* Booking CTA lives IN the hero band — the standalone top-actions
             row cost a full row of vertical space. Hidden on narrow screens
             by .vt-hero-cta's breakpoint; the bottom action-row CTA remains
             the booking path there. */
          <button
            type="button"
            className={`vt-hero-cta${feedback ? " vt-cta-success" : blocked ? " vt-cta-blocked" : ""}`}
            onClick={addBlock ? showAddBlock : handleAdd}
            disabled={pending}
            aria-disabled={blocked || undefined}
          >
            {ctaLabel}
          </button>
        }
      />

      <div className="vt-service-body">
      {/* Contextual notices only — the CTA moved into the hero band, and an
          empty bar would just waste the row it used to occupy. */}
      {(inCartSummary || capacityWarning) && (
        <div className="vt-top-actions">
          {inCartSummary}
          {capacityWarning}
        </div>
      )}
      {service.description && (
        <p className="vt-service-description">{service.description}</p>
      )}

      {showModeToggle && !matchedEvent && (
        <ModeToggle mode={mode} onChange={setMode} />
      )}

      {matchedEvent ? (
        <>
          <BookingSetup
            service={service}
            window={window}
            selectedEventUnit={selectedEventUnit}
            onChange={setWindow}
            onSeed={setSeededStartsAt}
            busyIntervals={busyIntervals}
            serviceHours={serviceHours}
            capacityRemainingText={capacityRemainingText}
            showStatusPills={false}
            // The date's own picker below owns the party, in every layout: the
            // generic stepper let a party of 5 through on a 2/4/6/8 date.
            hidePartyStepper
          />
          <FixedMenuBanner event={matchedEvent} />
          {overrunNotice && (
            <p className="vt-fixed-overrun" role="note">
              {overrunNotice}
            </p>
          )}
          <FixedMenuSummary menu={matchedEvent.menu} />
          {/* Variable range with a menu choice: the split IS the count, so
              the party stepper would be a second, disagreeing control. */}
          {!(menuChoice && matchedEvent.guest_selection_mode === "variable") && (
            <FixedPartyPicker
              event={matchedEvent}
              hideTotal={menuChoice}
              // Variable events have no party_size_options[0]; seed from the
              // range minimum so the initial value is in-bounds.
              value={
                window?.party_size ??
                (matchedEvent.guest_selection_mode === "variable"
                  ? matchedEvent.party_size_min ?? 2
                  : matchedEvent.party_size_options[0] ?? 2)
              }
              onChange={(n) => {
                if (!window) return;
                setPartySnap(null);
                setWindow({ ...window, party_size: n });
                setSeatSelection(null);
                setMenuSplit({});
              }}
            />
          )}
          {partySnapNote && (
            <p className="vt-party-snap-note" role="status">
              {partySnapNote}
            </p>
          )}
          {menuChoice && window && (
            <GuestMenuSplitPicker
              event={matchedEvent}
              partySize={window.party_size}
              split={menuSplit}
              onChange={(next) => {
                setMenuSplit(next);
                if (matchedEvent.guest_selection_mode === "variable") {
                  // the split is the party; keep the window in step so the
                  // hold's party equals what the guest chose
                  const total = splitTotal(next);
                  setWindow({ ...window, party_size: Math.max(1, total) });
                }
              }}
            />
          )}
          {matchedEvent.floor_plan_id && (
            <FloorPlanPicker
              service={service}
              selected={seatSelection}
              onSelect={setSeatSelection}
              unavailableElementIds={unavailable}
              activePlanId={effectivePlanId}
              onActivePlanChange={(id) => {
                setActivePlanId(id);
                setSeatSelection(null);
              }}
            />
          )}
          {capacity && capacity.ok === false && (
            <div className="vt-capacity-warning">
              {describeReason(capacity.reason)}
              {window != null &&
                ["service_capacity_exceeded", "plan_capacity_exceeded", "rooms_sold_out", "unit_sold_out", "no_capacity"].includes(
                  String(capacity.reason),
                ) && <WaitlistPrompt serviceId={service.id} window={window} />}
            </div>
          )}
        </>
      ) : mode === "seat" && hasFloorPlans ? (
        <>
          {/* Floor-plan view stacks vertically: WHEN/DURATION/PARTY row +
              dine enhancements on top, floor plan below. See styles.css
              .vt-seat-layout for why this isn't a 2-col split. */}
          <div className="vt-seat-layout">
            <div className="vt-seat-layout-inputs">
              <BookingSetup
                service={service}
                window={window}
                selectedEventUnit={selectedEventUnit}
                onChange={setWindow}
            onSeed={setSeededStartsAt}
                busyIntervals={busyIntervals}
                serviceHours={serviceHours}
                capacityRemainingText={capacityRemainingText}
                showStatusPills={false}
                seatingField={seatingField}
                seatingPreview={seatingPreview}
              />
              {showSeatingPicker && !inlineSeating && (
                <SeatingAreaPicker
                  serviceId={service.id}
                  selectedAreaId={selectedSeatingAreaId}
                  onAreaChange={handleAreaChange}
                />
              )}
              {service.service_type === "dining" && (
                <DineEnhancements
                  serviceId={service.id}
                  selectedSubcategoryId={selectedSubcategoryId}
                  onSubcategoryChange={setSelectedSubcategoryId}
                />
              )}
            </div>
            <FloorPlanPicker
              service={service}
              selected={seatSelection}
              onSelect={setSeatSelection}
              unavailableElementIds={unavailable}
              activePlanId={effectivePlanId}
              onActivePlanChange={(id) => {
                setActivePlanId(id);
                setSeatSelection(null);
              }}
            />
          </div>
          {capacity && capacity.ok === false && (
            <div className="vt-capacity-warning">
              {describeReason(capacity.reason)}
              {window != null &&
                ["service_capacity_exceeded", "plan_capacity_exceeded", "rooms_sold_out", "unit_sold_out", "no_capacity"].includes(
                  String(capacity.reason),
                ) && <WaitlistPrompt serviceId={service.id} window={window} />}
            </div>
          )}
        </>
      ) : mode === "general" ? (
        <>
          <BookingSetup
            service={service}
            window={window}
            selectedEventUnit={selectedEventUnit}
            onChange={setWindow}
            onSeed={setSeededStartsAt}
            busyIntervals={busyIntervals}
            serviceHours={serviceHours}
            capacityRemainingText={capacityRemainingText}
            showStatusPills={false}
            seatingField={seatingField}
            seatingPreview={seatingPreview}
            // The reservation's CTA lives IN the date/party row — a general
            // reservation has no option cards, so a separate bottom button
            // was the "it gets lost" step.
            ctaSlot={renderCta()}
          />
          {showSeatingPicker && !inlineSeating && (
            <SeatingAreaPicker
              serviceId={service.id}
              selectedAreaId={selectedSeatingAreaId}
              onAreaChange={handleAreaChange}
            />
          )}
          {service.service_type === "dining" && (
            <DineEnhancements
              serviceId={service.id}
              selectedSubcategoryId={selectedSubcategoryId}
              onSubcategoryChange={setSelectedSubcategoryId}
            />
          )}
          <div className="vt-general-hint">
            No specific table — staff will seat your party on arrival.
          </div>
          {capacity && capacity.ok === false && (
            <div className="vt-capacity-warning">
              {describeReason(capacity.reason)}
              {window != null &&
                ["service_capacity_exceeded", "plan_capacity_exceeded", "rooms_sold_out", "unit_sold_out", "no_capacity"].includes(
                  String(capacity.reason),
                ) && <WaitlistPrompt serviceId={service.id} window={window} />}
            </div>
          )}
        </>
      ) : tabledService ? (
        <EmptyUnits />
      ) : service.units.length === 0 ? (
        <EmptyUnits />
      ) : (
        <>
          {/* WHEN / DURATION / PARTY row. Required for every list-mode
              service (tastings, tours, picnics, custom) — the cart
              hold needs a starts_at / ends_at and party_size to be
              valid. Rendered first because the prototype shows the
              WHEN row above the option grid, and so the time gates
              any unit-specific availability check. For per_person
              multi-select the per-option guest steppers own the
              headcount, so the shared Party stepper is hidden. */}
          <BookingSetup
            service={service}
            window={window}
            selectedEventUnit={selectedEventUnit}
            onChange={setWindow}
            onSeed={setSeededStartsAt}
            busyIntervals={busyIntervals}
            serviceHours={serviceHours}
            capacityRemainingText={capacityRemainingText}
            showStatusPills={false}
            seatingField={seatingField}
            seatingPreview={seatingPreview}
          />
          {showSeatingPicker && !inlineSeating && (
            <SeatingAreaPicker
              serviceId={service.id}
              selectedAreaId={selectedSeatingAreaId}
              onAreaChange={handleAreaChange}
            />
          )}
          {/* The Options grid is suppressed for scheduled_sessions — the
              session tabs in BookingSetup already act as the unit picker,
              so showing the cards too would give the customer two ways to
              pick the same thing. (Quantity lives on each card's own
              stepper; scheduled sessions take the bottom CTA instead.) */}
          {!isScheduledSessions && (
            <>
              {/* Attribute chips, for a service whose units are backed by
                  attributed stock. A room is chosen by name, so a dozen room
                  cards are fine; a bike is chosen by KIND, and a dozen cards
                  that differ only in frame and pedals is a scrolling exercise.
                  The chips narrow the same grid — they are a filter over units,
                  not a second way to book, so nothing downstream changes.
                  Renders only when the stock actually has attributes to offer,
                  which means every service that exists today shows nothing. */}
              {facetKeys.length > 0 && (
                <div className="vt-unit-facets">
                  {facetKeys.map(({ key, label, values }) => (
                    <div className="vt-unit-facet" key={key}>
                      <span className="vt-unit-facet-label">{label}</span>
                      <div className="vt-unit-chips">
                        <button
                          type="button"
                          className={
                            "vt-unit-chip" +
                            (facetPicks[key] == null ? " vt-unit-chip-active" : "")
                          }
                          aria-pressed={facetPicks[key] == null}
                          onClick={() =>
                            setFacetPicks((p) => {
                              const n = { ...p };
                              delete n[key];
                              return n;
                            })
                          }
                        >
                          Any
                        </button>
                        {values.map((v) => {
                          // Grey a chip that cannot combine with the picks
                          // already made, rather than letting the guest choose
                          // it and land on an empty grid.
                          const reachable = service.units.some((u) => {
                            const a = unitAttrs[u.id];
                            if (!a || a[key] !== v) return false;
                            return Object.entries(facetPicks).every(
                              ([k2, v2]) => k2 === key || a[k2] === v2,
                            );
                          });
                          const on = facetPicks[key] === v;
                          return (
                            <button
                              type="button"
                              key={v}
                              className={
                                "vt-unit-chip" + (on ? " vt-unit-chip-active" : "")
                              }
                              aria-pressed={on}
                              disabled={!reachable && !on}
                              onClick={() =>
                                setFacetPicks((p) => ({ ...p, [key]: v }))
                              }
                            >
                              {v}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="vt-divider">
                Options{isMultiSelect ? " — pick one or more" : ""}
              </div>
              <div className="vt-option-cards">
                {visibleUnits.map((unit) => (
                  <UnitCard
                    key={unit.id}
                    unit={unit}
                    // Mixed grid (some rooms photographed, some not): give the
                    // unphotographed ones a name panel so the row stays even.
                    heroPlaceholder={service.units.some((u) => u.image_url)}
                    payAtVenue={service.pay_at_venue === true}
                    priceSuffix={
                      service.show_prices_in_widget === false ? "" : optionPriceSuffix(unit)
                    }
                    displayPrice={
                      unit.base_price != null ? unitDisplayPrice(unit) : null
                    }
                    // Same leak as the availability panel had: the card fell
                    // back to base_price, so a venue with prices switched off
                    // still had them on every option.
                    hidePrice={service.show_prices_in_widget === false}
                    unavailable={isUnitUnavailable(unit)}
                    unpriced={
                      service.service_type === "accommodation" &&
                      unit.base_price == null
                    }
                    selected={
                      isMultiSelect
                        ? (unitQty[unit.id] ?? 0) > 0
                        : unit.id === selectedUnitId
                    }
                    onSelect={
                      isMultiSelect
                        ? () => toggleUnit(unit.id)
                        : () => setSelectedUnitId(unit.id)
                    }
                    multiSelect={isMultiSelect}
                    // With per-card CTAs the pending-count map backs EVERY
                    // card's stepper (single-select included) — the count the
                    // foot Add will hold. Events keep the legacy gating.
                    quantity={
                      hasCardCtas || isMultiSelect
                        ? (unitQty[unit.id] ?? 0)
                        : null
                    }
                    onQuantityChange={
                      hasCardCtas || isMultiSelect
                        ? (nq) => setUnitQtyFor(unit.id, nq)
                        : undefined
                    }
                    qtyLabel={
                      isAccommodation
                        ? "rooms"
                        : unitBasis(unit) === "per_person"
                          ? "guests"
                          : "qty"
                    }
                    maxQuantity={
                      isAccommodation && (unit.inventory_count ?? 0) > 0
                        ? unit.inventory_count ?? undefined
                        : undefined
                    }
                    minQuantity={unitMin(unit)}
                    partyNoun={guestNoun(service)}
                    onOpenDetail={setGalleryUnit}
                    cardCta={
                      // Physical/seat units pin qty to 1 and book via their
                      // own flows — no card CTA for them (defensive; such
                      // services are tabled and never reach this branch).
                      hasCardCtas &&
                      !(serviceHasUniqueUnits && !isAccommodation)
                        ? {
                            inCartCount: unitCartCount(unit.id),
                            busy: pendingUnitId === unit.id,
                            // Date-first: no window yet → the card button reads
                            // "Pick a date first" and the stepper hides (a qty
                            // is meaningless before dates). onAdd's !window
                            // guard below makes the click a pure nudgeSetup().
                            // Known-full at the picked time: the same, and the
                            // button says so instead of a plain "Add".
                            needsDate: !window || capacityFull,
                            needsDateLabel:
                              window && capacityFull
                                ? isAccommodation
                                  ? "Full on these dates"
                                  : "Full at this time"
                                : undefined,
                            onAdd: () => {
                              if (
                                !window ||
                                (capacity && capacity.ok === false)
                              ) {
                                nudgeSetup();
                                return;
                              }
                              if (isUnitUnavailable(unit)) return;
                              void addUnitToCart(unit, unitQty[unit.id] ?? 1);
                            },
                            onRemove: () => removeUnitFromCart(unit),
                            onCheckout: onOpenCart,
                          }
                        : undefined
                    }
                  />
                ))}
              </div>
            </>
          )}
        </>
      )}

      {capacityWarning}

      {error && (
        <HoldErrorBanner
          error={error}
          errorEnquiryCta={errorEnquiryCta}
          onEnquire={onEnquire}
        />
      )}

      {/* Bottom row: rich status pill on the left + (for modes without
          per-card CTAs) the inline CTA on the right. Matches the prototype's
          `.actions` pattern where the status pill carries the selection
          breakdown ("Standard Tasting · 45 min · R 175 × 2 guests = R 350")
          and the CTA stays a short action verb ("Book tasting →"). Falls
          back to a plain text label for waiting states (no unit picked yet
          etc.) — `buildStatusBreakdown` returns null in that case. */}
      {multiOverPicked && window && (
        <div className="vt-capacity-warning" role="status">
          You&rsquo;ve picked {multiHeadcount} for {window.party_size} guest
          {window.party_size === 1 ? "" : "s"} — that&rsquo;s fine if some are
          doing more than one, just checking it&rsquo;s what you want.
        </div>
      )}
      {roomsUnderCapacity && window && (
        <div className="vt-capacity-warning" role="status">
          Your picked rooms sleep {multiRoomCapacity}, but you have{" "}
          {window.party_size} guests — add another room or a larger type so
          everyone fits.
        </div>
      )}
      {/* The standalone list-mode qty stepper is gone — quantity now lives ON
          each option card (per-card stepper + Add), so a bottom stepper would
          be a second, disconnected place to set the same number. */}
      <div className="vt-action-row">
        {(() => {
          // #351 multi-select: the per-option cards carry each price, so the
          // pill summarises the whole selection (count · guests/items · total).
          if (isMultiSelect) {
            return (
              <div className="vt-status-pill" role="status">
                <span className="vt-status-pill-dot" aria-hidden="true" />
                <span className="vt-status-pill-chips">
                  {tickedUnits.length === 0
                    ? "No options picked yet"
                    : `${window ? `${window.party_size} guest${window.party_size === 1 ? "" : "s"} · ` : ""}${multiCountTotal} picked`}
                </span>
                {tickedUnits.length > 0 && multiPriceTotal > 0 && (
                  <span className="vt-status-pill-total">
                    = {multiCurrency} {multiPriceTotal.toLocaleString()}
                  </span>
                )}
              </div>
            );
          }
          const breakdown = buildStatusBreakdown(
            service,
            mode,
            effectiveUnit,
            seatSelection,
            qty,
            window,
            addDisabled,
            pending,
          );
          if (!breakdown) {
            return (
              <div className="vt-action-status">
                {buildStatusLabel(
                  service,
                  mode,
                  effectiveUnit,
                  seatSelection,
                  addDisabled,
                  pending,
                )}
              </div>
            );
          }
          return (
            <div className="vt-status-pill" role="status">
              <span className="vt-status-pill-dot" aria-hidden="true" />
              <span className="vt-status-pill-chips">
                {breakdown.chips.join(" · ")}
              </span>
              {breakdown.total && (
                <span className="vt-status-pill-total">
                  = {breakdown.total}
                </span>
              )}
            </div>
          );
        })()}
        {/* Modes with per-card CTAs add from the cards, so the bottom CTA
            drops — EXCEPT when the tenant allows a no-option reservation,
            whose deposit-only hold has no card to add from (and the hero CTA
            is hidden on narrow screens). Every other mode keeps it. */}
        {(!hasCardCtas || allowNoOption) && ctaButton}
      </div>
      {hostedEvents && hostedEvents.length > 0 && onSelectHostedEvent && (
        <HostedEventsSection events={hostedEvents} onSelect={onSelectHostedEvent} />
      )}
      </div>
      </div>
      {galleryUnit && (
        <RoomGalleryModal
          unit={galleryUnit}
          bookDisabled={isUnitUnavailable(galleryUnit)}
          // Off: say why rather than an off "Book this room".
          bookLabel={
            isUnitUnavailable(galleryUnit)
              ? isAccommodation
                ? "Not available for these dates"
                : "Not available at this time"
              : undefined
          }
          onClose={() => setGalleryUnit(null)}
          onBook={() => {
            if (isUnitUnavailable(galleryUnit)) return;
            // Multi-select tracks selection in unitQty, not selectedUnitId — an
            // image/gallery option opens this modal instead of toggling on card
            // click, so "Book" here must tick it (else it's a dead CTA).
            if (isMultiSelect) setUnitQtyFor(galleryUnit.id, 1);
            else setSelectedUnitId(galleryUnit.id);
            setGalleryUnit(null);
          }}
        />
      )}

      {dateConfirm && (
        <SeededDateConfirmModal
          dateConfirm={dateConfirm}
          accentRef={panelRef}
          isAccommodation={isAccommodation}
          serviceName={service.name}
          onCancel={() => setDateConfirm(null)}
        />
      )}
    </section>
  );
}

function buildStatusLabel(
  service: WidgetService,
  mode: PickerMode,
  effectiveUnit: WidgetUnit | null,
  seatSelection: SeatSelection | null,
  addDisabled: boolean,
  pending: boolean,
): string {
  if (pending) return "Holding your slot…";
  if (mode === "seat" && !seatSelection) return "No table selected";
  if (service.units.length > 0 && !effectiveUnit && mode !== "seat") {
    return service.service_type === "tasting"
      ? "Pick a tasting"
      : service.service_type === "tour"
        ? "Pick a tour"
        : "Pick an option";
  }
  if (addDisabled) return "Choose date + party";
  return "Ready to book";
}

/**
 * Rich breakdown of the current selection — chips + optional total —
 * shown in the status pill above the inline CTA. Mirrors the
 * prototype's bottom-bar pattern:
 *
 *   ● Standard Tasting · 45 min · R 175 × 2 guests = R 350
 *
 * Returns null when the customer hasn't picked enough to compute a
 * line (waiting states; the simpler `buildStatusLabel` text covers
 * those cases). The caller falls back to that text label.
 *
 * Math intentionally mirrors `buildCtaLabel` — every chip the
 * customer sees should match what they get charged.
 */
function buildStatusBreakdown(
  service: WidgetService,
  mode: PickerMode,
  effectiveUnit: WidgetUnit | null,
  seatSelection: SeatSelection | null,
  qty: number,
  window: BookingWindow | null,
  addDisabled: boolean,
  pending: boolean,
): { chips: string[]; total: string | null } | null {
  if (pending) return null;
  if (addDisabled) return null;

  // Mode-by-mode label assembly.
  const chips: string[] = [];
  let total: string | null = null;

  if (mode === "seat") {
    if (!seatSelection || !window) return null;
    const seatLabel = seatSelection.element.label ?? effectiveUnit?.name ?? "Seat";
    chips.push(seatLabel);
    chips.push(humanDuration(window));
    chips.push(humanParty(window.party_size));
    if (effectiveUnit?.base_price != null) {
      const t = Number(effectiveUnit.base_price) * window.party_size;
      total = `${effectiveUnit.currency ?? "ZAR"} ${t.toLocaleString()}`;
    }
    return { chips, total };
  }

  if (mode === "general") {
    if (!window) return null;
    chips.push(service.name);
    chips.push(humanDuration(window));
    chips.push(humanParty(window.party_size));
    // General reservations are typically pay-at-the-venue; no total.
    return { chips, total: null };
  }

  // mode === "list"
  if (!effectiveUnit) return null;
  chips.push(effectiveUnit.name);
  // Tastings, tours, picnics carry a duration on the booking window;
  // accommodation shows the stay length in nights, not hours.
  if (window) {
    chips.push(
      service.service_type === "accommodation"
        ? humanStayNights(window)
        : humanDuration(window),
    );
  }
  if (effectiveUnit.base_price != null && qty > 0) {
    const unitPrice = Number(effectiveUnit.base_price);
    const currency = effectiveUnit.currency ?? "ZAR";
    if (qty > 1) {
      chips.push(`${currency} ${unitPrice.toLocaleString()} × ${qty}`);
      total = `${currency} ${(unitPrice * qty).toLocaleString()}`;
    } else {
      total = `${currency} ${unitPrice.toLocaleString()}`;
    }
  }
  return { chips, total };
}

function humanDuration(window: BookingWindow): string {
  const minutes = Math.max(
    0,
    Math.round((Date.parse(window.ends_at) - Date.parse(window.starts_at)) / 60000),
  );
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

// Accommodation stays are measured in nights (check-in → check-out), not hours.
export function humanStayNights(window: BookingWindow): string {
  const minutes = Math.max(
    0,
    Math.round((Date.parse(window.ends_at) - Date.parse(window.starts_at)) / 60000),
  );
  const nights = Math.max(1, Math.round(minutes / 1440));
  return `${nights} night${nights === 1 ? "" : "s"}`;
}

function humanParty(n: number): string {
  return `${n} guest${n === 1 ? "" : "s"}`;
}

// ─────────────────────────────────────────────────────────────────────────────

function ModeToggle({
  mode,
  onChange,
}: {
  mode: PickerMode;
  onChange: (m: PickerMode) => void;
}) {
  return (
    <div className="vt-plan-mode" role="tablist" aria-label="Choose booking type">
      <button
        type="button"
        role="tab"
        aria-selected={mode === "general"}
        className={`vt-plan-mode-btn${mode === "general" ? " vt-plan-mode-active" : ""}`}
        onClick={() => onChange("general")}
      >
        Any available table
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={mode === "seat"}
        className={`vt-plan-mode-btn${mode === "seat" ? " vt-plan-mode-active" : ""}`}
        onClick={() => onChange("seat")}
      >
        Pick a specific table
      </button>
    </div>
  );
}

// "What's on" — ticketed events hosted by this service. Tapping a card opens
// that event's detail in the Events tab (so hosted events live under BOTH the
// service and the tab).
function HostedEventsSection({
  events,
  onSelect,
}: {
  events: WidgetEvent[];
  onSelect: (eventId: string) => void;
}) {
  // Category filter — mirrors the Events tab so a service that hosts events
  // (or an event-only venue like the Auditorium) lets guests narrow "What's
  // on" by type. Only shown when the events actually span 2+ categories.
  const categories = useMemo(() => collectEventCategories(events), [events]);
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);
  // One clock for every card: a sitting whose sales close while the list is
  // open turns to "Bookings closed" without a reload.
  const now = useNow(60_000);
  const visible = activeCategoryId
    ? events.filter((e) => e.category_id === activeCategoryId)
    : events;
  return (
    <div className="vt-hosted-events">
      <div className="vt-divider">What&rsquo;s on</div>
      {categories.length > 1 && (
        <div
          className="vt-events-filters"
          role="group"
          aria-label="Filter events by category"
        >
          <button
            type="button"
            className={`vt-events-chip${activeCategoryId === null ? " vt-events-chip-active" : ""}`}
            onClick={() => setActiveCategoryId(null)}
          >
            All
          </button>
          {categories.map((c) => (
            <button
              key={c.id}
              type="button"
              className={`vt-events-chip${activeCategoryId === c.id ? " vt-events-chip-active" : ""}`}
              onClick={() => setActiveCategoryId(c.id)}
            >
              {c.name}
            </button>
          ))}
        </div>
      )}
      <ul className="vt-hosted-events-grid">
        {visible.map((e) => (
          <HostedEventCard
            key={e.id}
            event={e}
            now={now}
            onSelect={() => onSelect(e.id)}
          />
        ))}
      </ul>
    </div>
  );
}

// A hosted event rendered with the same card system as the Events tab, so
// "What's on" under a service matches the rest of the widget. Tapping Book
// opens the event's detail in the Events tab (onSelect -> onSelectHostedEvent),
// which works with that tab hidden too.
function HostedEventCard({
  event,
  now,
  onSelect,
}: {
  event: WidgetEvent;
  now: number;
  onSelect: () => void;
}) {
  // The cheapest menu a group books, never the kids or vegetarian menu that
  // goes alongside it (eventFromPriceCents).
  const fromCents = eventFromPriceCents(event, now);
  const from =
    fromCents != null && fromCents > 0
      ? `${event.currency ?? "ZAR"} ${(fromCents / 100).toLocaleString()}`
      : null;
  // Mirror the Events-tab EventCard: never offer Book on a sold-out event.
  // Seats (20261003220010): "X left" and "Sold out" count seats only.
  const seats = eventSeatState(event);
  const { remaining, lowStock } = seats;
  // Mirror the Events-tab EventCard: a private sitting another group has
  // taken reads "Booked" and can't be picked (event menus, 20260930100010).
  const cart = useCart();
  // Seats gone, and none of them this guest's (as the Events-tab card).
  const soldOut =
    seats.soldOut && seatsInCart(cart.items, event.id, event.ticket_types ?? []) === 0;
  const booked = bookedByAnother(event, ticketsInCart(cart.items, event.id));
  // Sales have closed: say so here, not after the guest tries
  // (event_sales_closed).
  const closed = salesClosed(event, now);
  const heroUrl = eventHeroUrl(event);
  return (
    <li
      className={`vt-event-card${soldOut ? " vt-event-sold-out" : ""}${booked ? " vt-event-booked" : ""}${closed ? " vt-event-closed" : ""}${heroUrl ? " vt-event-card-has-image" : ""}`}
    >
      {heroUrl && (
        <div className="vt-event-card-hero">
          <img
            className="vt-cover-img"
            src={optimizedImageUrl(heroUrl, { width: 800 })}
            srcSet={optimizedSrcSet(heroUrl, IMG_WIDTHS.CARD)}
            sizes="(min-width: 900px) 33vw, (min-width: 600px) 50vw, 100vw"
            alt=""
            loading="lazy"
            decoding="async"
          />
          <span className="vt-event-card-hero-name">{event.name}</span>
        </div>
      )}
      <div className="vt-event-card-body">
        <EventBadges event={event} booked={booked} />
        <div className="vt-event-date-line">
          {humanWindow(event.starts_at, event.ends_at)}
        </div>
        {!heroUrl && <h3 className="vt-event-name">{event.name}</h3>}
        <SetMenuBlock menu={event.set_menu} compact />
        {event.summary && <p className="vt-event-summary">{event.summary}</p>}
        <button
          type="button"
          className="vt-event-card-button"
          onClick={onSelect}
          disabled={soldOut || booked || closed}
        >
          {soldOut ? (
            "Sold out"
          ) : booked ? (
            "Booked"
          ) : closed ? (
            "Bookings closed"
          ) : lowStock ? (
            <>Book ({remaining} left) &rarr;</>
          ) : from ? (
            <>Book from {from} &rarr;</>
          ) : (
            <>Book &rarr;</>
          )}
        </button>
      </div>
    </li>
  );
}

// Panel for a venue that takes no direct bookings but hosts ticketed events
// (e.g. The Audrium). Shows the hero + description + "What's on" events
// (bookable) + an enquiry CTA for private events, instead of the dead-end
// "online bookings aren't open" message.
function EventVenuePanel({
  service,
  activeBrand,
  heroBrand,
  heroStyle,
  panelStyle,
  events,
  onSelectEvent,
  onEnquire,
}: {
  service: WidgetService;
  activeBrand: WidgetBrand | null;
  heroBrand?: WidgetBrand | null;
  heroStyle?: CSSProperties;
  panelStyle?: CSSProperties;
  events: WidgetEvent[];
  onSelectEvent?: (eventId: string) => void;
  onEnquire?: () => void;
}) {
  const heroImage =
    service.hero_image_url ?? (heroBrand ?? activeBrand)?.logo_url ?? null;
  return (
    <section className="vt-panel" style={panelStyle}>
      <div className="vt-service-card">
        <div className="vt-hero" style={heroStyle}>
          {heroImage ? (
            <img
              src={optimizedImageUrl(heroImage, { width: 1280 })}
              srcSet={optimizedSrcSet(heroImage, IMG_WIDTHS.HERO)}
              sizes="100vw"
              alt={service.name}
              decoding="async"
            />
          ) : (
            <div className={`vt-hero-gradient vt-hero-${service.service_type}`} />
          )}
          <div className="vt-hero-overlay">
            <div className="vt-hero-overlay-text">
              <h2>{service.name}</h2>
              {service.tagline && <p>{service.tagline}</p>}
            </div>
          </div>
        </div>
        <div className="vt-service-body">
          {service.description && (
            <p className="vt-service-description">{service.description}</p>
          )}
          {events.length > 0 && onSelectEvent ? (
            <HostedEventsSection events={events} onSelect={onSelectEvent} />
          ) : (
            <p className="vt-hosted-events-empty">
              No upcoming public events right now — check back soon.
            </p>
          )}
          {onEnquire && (
            <div className="vt-event-venue-enquire">
              <p>Planning a private event or function here?</p>
              <button type="button" className="vt-cta-inline" onClick={onEnquire}>
                Enquire about private events &rarr;
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
// Why Add can't hold yet (ServicePanel's addBlock): a date, a seat on the
// plan, room at that time, a menu for every guest, an option, or an option
// whose own stock is gone.
type AddBlock = "when" | "seat" | "capacity" | "menus" | "options" | "stock";

// Capacity refusals that mean "no room at that time".
const FULL_REASONS = new Set([
  "service_capacity_exceeded",
  "plan_capacity_exceeded",
  "rooms_sold_out",
  "unit_sold_out",
  "no_capacity",
]);

// The CTA's words when the capacity check says no. Short: the full sentence
// is the warning under the setup row (describeReason). Only a real "full"
// says Full; a failed check or another refusal says it can't be booked.
function capacityCtaLabel(reason: unknown, isAccommodation: boolean): string {
  const r = String(reason);
  if (r === "seat_taken") return "That table is taken: pick another";
  if (r === "unit_capacity_short") return "Too many guests for this option";
  if (r === "seating_area_full") return "That area is full: pick another";
  if (FULL_REASONS.has(r)) {
    return isAccommodation ? "Full on these dates: pick others" : "Full at this time: pick another";
  }
  return "Not available: change your choice";
}

function EmptyUnits() {
  return <div className="vt-empty">No bookable options configured yet.</div>;
}

// Short action-verb CTA. The selection breakdown (unit, duration,
// party, total) lives in the status pill to its left, so the button
// itself stays terse. Falls back to verbose copy for waiting states
// where the pill is hidden and the button needs to explain what's
// missing.
function buildCtaLabel(
  service: WidgetService,
  mode: PickerMode,
  unit: WidgetUnit | null,
  _qty: number,
  seatSelection: SeatSelection | null,
  window: BookingWindow | null,
): string {
  if (mode === "seat") {
    if (!window) return "Set your booking window";
    if (!seatSelection) return `Select a table on ${service.name}`;
    return "Hold table →";
  }
  if (mode === "general") {
    if (!window) return "Set your booking window";
    return "Book →";
  }
  if (!unit) {
    return service.service_type === "tasting"
      ? "Book tasting →"
      : `Add ${service.name} →`;
  }
  return service.service_type === "tasting" ? "Book tasting →" : "Add to visit →";
}

function buildFixedMenuCtaLabel(
  event: WidgetSpecialDateEvent,
  partySize: number,
  seatSelection: SeatSelection | null,
  split: GuestMenuSplit,
): string {
  if (event.floor_plan_id && !seatSelection) {
    return `Pick a seat on the plan above`;
  }
  // A date with a menu choice: the CTA reads the gap until the split adds up.
  const blocker = specialDateAddBlocker(event, partySize, split);
  if (blocker) return blocker;
  const guests = offersMenuChoice(event) && event.guest_selection_mode === "variable"
    ? splitTotal(split)
    : partySize;
  const total = specialDateTotalCents(event, partySize, split) / 100;
  return `Pay ${event.currency} ${total.toLocaleString()} · ${guests} guest${guests === 1 ? "" : "s"}`;
}

